import { useAtom } from "jotai";
import toast from "react-hot-toast";
import classNames from "classnames";
import axiosInstance from "helpers/api";
import llmAxiosInstance from "helpers/llmApi";
import { UserInfo } from 'interfaces/user';
import { user_info } from "jotai/UserJotaiInstance";
import { SimpleBuyerProfileProps } from "interfaces/buyer";
import BuyerProfileModal from 'components/modals/BuyerProfileModal';
// DEPRECATED: Replaced with AI-powered profile builder
// import BuyerProfileTab from './BuyerProfileTab';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Logo import temporarily commented out due to bundling issues
// import KairosIQLogo from "../../../assets/img/logos/KairosIQ-LogoOnly.png";
import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Spinner, Badge, Button, Modal, Form, Nav, Tab } from "react-bootstrap";
import { LeadAiAssistantProps, MarketStatProps, PropertyPreferenceProps, CommunicationItemProps, KeyInsightProps, RecommendedActionProps } from "interfaces/leadAi";
import {
  faChartLine,
  faClock,
  faEnvelope,
  faEye,
  faMapMarkerAlt,
  faHome,
  faUser,
  faExclamationTriangle,
  faCircle,
  faCheckCircle,
  faLightbulb,
  faPlus,
  faBolt,
  faEdit,
  faCalendar,
  faUpload,
  faFileAlt,
  faBuilding,
  faDollarSign,
  faRefresh,
  faTasks,
  faHeart
} from "@fortawesome/free-solid-svg-icons";

// Seller Profile Interfaces
interface SellerProfile {
  id: string;
  lead_id: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_type: string;
  bedrooms: number | null;
  bathrooms: number | null;
  square_footage: number | null;
  year_built: number | null;
  estimated_value: number | null;
  desired_price: number | null;
  minimum_price: number | null;
  selling_timeframe: string;
  motivation_level: string;
  seller_stage: string;
  property_condition: string;
  recent_improvements: string | null;
  why_selling: string | null;
  created_at?: string;
  updated_at?: string;
}

interface PDFReport {
  id: string;
  lead_id: string;
  file_name: string;
  file_path: string;
  analysis_status: 'pending' | 'processing' | 'completed' | 'failed';
  ai_analysis: any;
  uploaded_at: string;
}

interface PropertyAnalysis {
  valuationConfidence: number;
  marketPosition: string;
  pricingStrategy: string;
  timeToSell: string;
  recommendations: string[];
  comparableProperties?: ComparableProperty[];
  marketStats?: MarketStatistics;
  subjectProperty?: SubjectProperty;
}

interface ComparableProperty {
  mls?: string;
  address: string;
  status: 'SOLD' | 'ACTIVE' | 'UNDER_CONTRACT' | 'EXPIRED';
  beds: number;
  baths: number;
  sqft: number;
  price: number;
  price_per_sqft: number;
  days_on_market: number;
}

interface MarketStatistics {
  sold_listings?: {
    count: number;
    avg_price: number;
    avg_price_per_sqft: number;
    avg_days_on_market: number;
    price_range?: { low: number; high: number };
  };
  active_listings?: {
    count: number;
    avg_price: number;
    avg_price_per_sqft: number;
    avg_days_on_market: number;
  };
  active_under_contract?: {
    count: number;
    avg_price: number;
    avg_price_per_sqft: number;
    avg_days_on_market: number;
  };
  expired_listings?: {
    count: number;
    avg_price: number;
    avg_price_per_sqft: number;
    avg_days_on_market: number;
  };
}

interface SubjectProperty {
  address: string;
  city: string;
  state: string;
  zip: string;
  beds: number;
  baths: number;
  sqft?: number;
  year_built?: number;
  property_type: string;
  lot_size?: string;
}

// US States array for dropdowns
const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];



const LeadAiAssistant: React.FC<LeadAiAssistantProps> = ({ leadDetails, className }) => {  
  // State management
  const [keyInsights, setKeyInsights] = useState<KeyInsightProps[]>([]);
  
  // Seller Profile State
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);
  const [uploadedReports, setUploadedReports] = useState<PDFReport[]>([]);
  const [aiPropertyAnalysis, setAiPropertyAnalysis] = useState<PropertyAnalysis | null>(null);
  const [loadingSellerProfile, setLoadingSellerProfile] = useState(false);
  const [loadingPropertyAnalysis, setLoadingPropertyAnalysis] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [activeTab, setActiveTab] = useState('market_intelligence');
  const [recentCommunications, setRecentCommunications] = useState<CommunicationItemProps[]>([]);
  const [propertyPreferences, setPropertyPreferences] = useState<PropertyPreferenceProps[]>([]);
  const [marketIntelligence, setMarketIntelligence] = useState<MarketStatProps[]>([]);
  const [buyerProfile, setBuyerProfile] = useState<SimpleBuyerProfileProps | null>(null);
  const [savedProperties, setSavedProperties] = useState<any[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [loadingMarket, setLoadingMarket] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    bedrooms: '',
    bathrooms: '',
    budget_min: '',
    budget_max: '',
    property_type: '',
    timeline: '',
    additional_notes: '',
    modalTab: 'manual', // 'manual' or 'ai'
    aiMessage: '',
    pre_approved: 'false',
    pre_approval_amount: '',
    first_time_buyer: 'false',
    buyer_stage: '',
    motivation: '',
    preferred_cities: '',
    square_feet_min: '',
    square_feet_max: '',
    desired_move_in_date: '',
    max_commute_time: '',
    work_location: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Seller Profile Modal state
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [isNewSellerLead, setIsNewSellerLead] = useState(false);
  const [sellerModalMode, setSellerModalMode] = useState('welcome'); // 'welcome' or 'form'
  const [sellerFormData, setSellerFormData] = useState({
    modalTab: 'manual',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    square_footage: '',
    year_built: '',
    estimated_value: '',
    desired_price: '',
    minimum_price: '',
    selling_timeframe: '',
    motivation_level: 'medium',
    seller_stage: 'exploring',
    property_condition: '',
    recent_improvements: '',
    why_selling: ''
  });
  const [savingSellerProfile, setSavingSellerProfile] = useState(false);



  const [leadMetrics, setLeadMetrics] = useState<{
    leadScore: number | null;
    daysActive: number | null;
    responseRate: number | null;
    aiConfidence: number | null;
    loading: boolean;
  }>({
    leadScore: null,
    daysActive: null,
    responseRate: null,
    aiConfidence: null,
    loading: true
  });

  interface AIIntelligenceMetric {
    score: number | null;
    label: string;
    description: string;
  }
  
  const [aiIntelligence, setAiIntelligence] = useState<{
    buyingIntent: AIIntelligenceMetric;
    engagementQuality: AIIntelligenceMetric;
    conversionProbability: AIIntelligenceMetric;
    loading: boolean;
  }>({
    buyingIntent: { score: null, label: 'Not Available', description: 'Loading...' },
    engagementQuality: { score: null, label: 'Not Available', description: 'Loading...' },
    conversionProbability: { score: null, label: 'Not Available', description: 'Loading...' },
    loading: true
  });

  // Add state for Recommended Actions
  const [recommendedActions, setRecommendedActions] = useState<{
    actions: RecommendedActionProps[];
    loading: boolean;
  }>({
    actions: [],
    loading: true
  });

  const [userInfo] = useAtom<UserInfo>(user_info);
  
  // Lead Type Detection - Match LeadDetailsTab.tsx logic
  const leadTypeString = leadDetails?.lead_type?.lead_type || leadDetails?.lead_type || "";
  const isBuyer = leadTypeString.toLowerCase().includes("buyer");
  const isSeller = leadTypeString.toLowerCase().includes("seller");
  const isInvestor = leadTypeString.toLowerCase().includes("investor");
  
  const visibleTabs = {
    showBuyer: isBuyer || isInvestor,
    showSeller: isSeller,
    showMarketIntel: true
  };
  
  console.log('🔍 [LEAD TYPE DEBUG] Detection results:', {
    leadTypeString,
    isBuyer,
    isSeller,
    isInvestor,
    visibleTabs,
    leadDetails_lead_type: leadDetails?.lead_type,
    leadDetails_lead_types_id: leadDetails?.lead_types_id
  });
  const isAuthorized = userInfo?.payment_status !== "Cancelled";

  // Real-time trigger system for lead_ai_context updates
  const [realTimeNotifications, setRealTimeNotifications] = useState<{
    hasNewRecommendations: boolean;
    hasContextUpdate: boolean;
    lastUpdateTime: Date | null;
    updateTrigger: number;
  }>({
    hasNewRecommendations: false,
    hasContextUpdate: false,
    lastUpdateTime: null,
    updateTrigger: 0
  });

  // Real-time context refresh function
  const triggerContextRefresh = async (reason: string = 'subscriber_activity', activityData?: any) => {    
    try {
      // Call the backend API to update AI context
      const response = await axiosInstance.post(`/leads/${leadDetails.id}/ai-context/refresh`, {
        activityType: reason,
        activityData: activityData || {},
        reason: reason
      });

      console.log('🎯 [AI CONTEXT API] Response:', response.data);

      // Update the trigger counter to force re-evaluation
      setRealTimeNotifications(prev => ({
        ...prev,
        updateTrigger: prev.updateTrigger + 1,
        lastUpdateTime: new Date()
      }));

      // Refresh all AI context data in memory
      try {
        const loadPromises = [
          loadLeadMetrics(),
          loadAiIntelligence(),
          loadRecommendedActions(),
          loadKeyInsights(),
          loadRecentCommunications(),
          loadPropertyPreferences(),
          loadMarketIntelligence()
        ];
        
        // Add seller-specific data loads if seller tab is visible
        if (visibleTabs.showSeller) {
          loadPromises.push(
            loadSellerProfile(),
            loadPropertyAnalysis()
          );
        }
        
        await Promise.all(loadPromises);
      } catch (refreshError) {
        console.error('⚠️ [AI CONTEXT REFRESH] Some data refresh failed:', refreshError);
      }

      // Show notification in AI Lead Intelligence panel
      setRealTimeNotifications(prev => ({
        ...prev,
        hasNewRecommendations: true,
        hasContextUpdate: true
      }));

      // Auto-clear notification after 10 seconds
      setTimeout(() => {
        setRealTimeNotifications(prev => ({
          ...prev,
          hasNewRecommendations: false,
          hasContextUpdate: false
        }));
      }, 10000);

      toast.success('🤖 AI Lead Intelligence Updated', {
        duration: 4000,
        position: 'top-right'
      });

    } catch (error: any) {
      console.error('❌ [AI CONTEXT REFRESH] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      toast.error(`Failed to refresh AI context: ${errorMessage}`, {
        duration: 5000,
        position: 'top-right'
      });
    }
  };

  // Initialize data when leadDetails are available
  useEffect(() => {
    if (leadDetails?.id) {
      // Set default active tab based on lead type
      if (visibleTabs.showBuyer && !visibleTabs.showSeller) {
        setActiveTab('buyer_profile');
      } else if (visibleTabs.showSeller && !visibleTabs.showBuyer) {
        setActiveTab('seller_profile');
      } else {
        setActiveTab('market_intelligence');
      }
      
      // Load buyer profile if buyer tab should be visible
      if (visibleTabs.showBuyer) {
        loadBuyerProfile();
      }
      
      // Load seller profile if seller tab should be visible
      if (visibleTabs.showSeller) {
        console.log('🔍 [INIT] Loading seller data for lead type:', leadTypeString);
        console.log('🔍 [INIT] Lead types ID:', leadDetails.lead_types_id);
        console.log('🔍 [INIT] Lead details:', leadDetails);
        
        // Clear any cached analysis first
        setAiPropertyAnalysis(null);
        setSellerProfile(null);
        
        loadSellerProfile();
        loadPropertyAnalysis();
      }
      
      // Load common data for all lead types
      loadLeadMetrics();
      loadAiIntelligence();
      loadRecommendedActions();
      loadKeyInsights();
      loadRecentCommunications();
      loadMarketIntelligence();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails?.id, leadTypeString]);

  // Monitor buyerProfile changes and refresh property preferences
  useEffect(() => {
    console.log('🔍 [DEBUG STATE] buyerProfile state changed:', buyerProfile);
    if (buyerProfile) {
      console.log('🔍 [DEBUG STATE] buyerProfile.id:', buyerProfile.id);
      console.log('🔍 [DEBUG STATE] buyerProfile.lead_id:', buyerProfile.lead_id);
      console.log('🔍 [DEBUG STATE] ID is present:', !!buyerProfile.id);
      
      // Refresh property preferences when buyer profile is loaded or updated
      loadPropertyPreferences();
    } else {
      console.log('🔍 [DEBUG STATE] buyerProfile is null/undefined');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerProfile]);


  // Load saved properties when component mounts or activeTab changes
  React.useEffect(() => {
    if (activeTab === 'buyer_profile' && leadDetails) {
      loadSavedProperties();
    }
  }, [activeTab, leadDetails]);

  // Load saved properties for the lead
  const loadSavedProperties = async () => {
    if (!leadDetails?.lead_email) return;
    
    try {
      // First try to get from local storage (properties saved from aisearchportal)
      const buyerContext = localStorage.getItem('portalBuyerContext');
      if (buyerContext) {
        const context = JSON.parse(buyerContext);
        if (context.leadId === leadDetails.id || context.email === leadDetails.lead_email) {
          const savedPropertiesData = JSON.parse(localStorage.getItem('savedPropertiesData') || '[]');
          setSavedProperties(savedPropertiesData);
          console.log('📍 Loaded saved properties from local storage:', savedPropertiesData.length);
        }
      }
      
      // Also check if there are any saved properties from the public portal
      const publicSavedData = JSON.parse(localStorage.getItem('savedPropertiesData') || '[]');
      const filteredByEmail = publicSavedData.filter((prop: any) => 
        prop.buyerEmail === leadDetails.lead_email || prop.buyerId === leadDetails.id
      );
      
      if (filteredByEmail.length > 0) {
        setSavedProperties(filteredByEmail);
        console.log('📍 Found saved properties for this lead:', filteredByEmail.length);
      }
    } catch (error) {
      console.error('Error loading saved properties:', error);
    }
  };

  const loadBuyerProfile = async () => {
    try {
      if (!leadDetails?.id) {
        console.warn('⚠️ [LOAD] No lead ID available, cannot load buyer profile');
        return;
      }
      
      console.log('🔍 [LOAD] Starting to load buyer profile for lead ID:', leadDetails.id);
      console.log('🔍 [LOAD] User ID:', userInfo?.id);
      console.log('🔍 [LOAD] Making API call to:', `/buyer-profiles/lead/${leadDetails.id}`);
      console.log('🔍 [LOAD] Auth token:', localStorage.getItem('auth_token'));
      console.log('🔍 [LOAD] Current hostname:', window.location.hostname);
      console.log('🔍 [LOAD] Axios base URL:', axiosInstance.defaults.baseURL);
      
      if (!userInfo?.id) {
        console.warn('⚠️ [LOAD] No user ID available, cannot load buyer profile');
        return;
      }
      
      console.log('🔍 [LOAD] Full URL will be:', `${axiosInstance.defaults.baseURL}buyer-profiles/lead/${leadDetails.id}?user_id=${userInfo.id}`);
      
      const response = await axiosInstance.get(`/buyer-profiles/lead/${leadDetails.id}`, {
        params: { user_id: userInfo.id }
      });
      
      console.log('📥 [LOAD] Raw buyer profile response:', response.data);
      console.log('📥 [LOAD] Response status:', response.status);
      console.log('📥 [LOAD] Response headers:', response.headers);
      console.log('📥 [LOAD] Response structure analysis:');
      console.log('  - response.data type:', typeof response.data);
      console.log('  - response.data.status:', response.data?.status);
      console.log('  - response.data.buyer_profile exists:', !!response.data?.buyer_profile);
      console.log('  - response.data.data exists:', !!response.data?.data);
      console.log('  - response.data.data.buyer_profile exists:', !!response.data?.data?.buyer_profile);
      
      // Handle different response structures from API - prioritize your exact structure
      let profileData = null;
      
      // Check for your exact response structure first: { data: { buyer_profile: {...} } }
      if (response.data?.data?.buyer_profile) {
        profileData = response.data.data.buyer_profile;
        console.log('📥 [LOAD] Using response.data.data.buyer_profile structure (primary)');
      } else if (response.data?.buyer_profile) {
        profileData = response.data.buyer_profile;
        console.log('📥 [LOAD] Using response.data.buyer_profile structure');
      } else if (response.data?.status === 'success' && response.data?.data?.buyer_profile) {
        profileData = response.data.data.buyer_profile;
        console.log('📥 [LOAD] Using success response with data.buyer_profile');
      } else if (response.data?.status === 'success' && response.data?.buyer_profile) {
        profileData = response.data.buyer_profile;
        console.log('📥 [LOAD] Using success response with buyer_profile');
      } else if (response.data?.data) {
        profileData = response.data.data;
        console.log('📥 [LOAD] Using response.data.data');
      } else if (response.data && typeof response.data === 'object' && response.data.id) {
        // Direct profile object response
        profileData = response.data;
        console.log('📥 [LOAD] Using direct response.data object');
      } else {
        console.log('📥 [LOAD] No recognized response structure');
        console.log('📥 [LOAD] Full response for debugging:', JSON.stringify(response.data, null, 2));
      }
      
      if (profileData) {
        console.log('✅ [LOAD] Setting buyer profile data:', profileData);
        console.log('🔍 [LOAD] Profile ID:', profileData.id);
        console.log('🔍 [LOAD] Profile lead_id:', profileData.lead_id);
        console.log('🔍 [LOAD] Profile property_preferences:', profileData.property_preferences);
        console.log('🔍 [LOAD] Profile property_preferences type:', typeof profileData.property_preferences);
        
        // Parse JSON strings if needed
        if (typeof profileData.property_preferences === 'string') {
          try {
            profileData.property_preferences = JSON.parse(profileData.property_preferences);
            console.log('📥 [LOAD] Parsed property_preferences from string');
          } catch (e) {
            console.error('❌ [LOAD] Failed to parse property_preferences:', e);
          }
        }
        if (typeof profileData.location_preferences === 'string') {
          try {
            profileData.location_preferences = JSON.parse(profileData.location_preferences);
            console.log('📥 [LOAD] Parsed location_preferences from string');
          } catch (e) {
            console.error('❌ [LOAD] Failed to parse location_preferences:', e);
          }
        }
        if (typeof profileData.lifestyle_preferences === 'string') {
          try {
            profileData.lifestyle_preferences = JSON.parse(profileData.lifestyle_preferences);
            console.log('📥 [LOAD] Parsed lifestyle_preferences from string');
          } catch (e) {
            console.error('❌ [LOAD] Failed to parse lifestyle_preferences:', e);
          }
        }
        
        setBuyerProfile(profileData);
        return profileData; // Return the loaded profile for immediate use
      } else {
        console.log('⚠️ [LOAD] No buyer profile found in response');
        setBuyerProfile(null);
        return null;
      }
    } catch (error: any) {
      console.error('❌ [LOAD] Error loading buyer profile:', error);
      console.error('❌ [LOAD] Error status:', error.response?.status);
      console.error('❌ [LOAD] Error data:', error.response?.data);
      console.error('❌ [LOAD] Request URL:', error.config?.url);
      console.error('❌ [LOAD] Request headers:', error.config?.headers);
      
      if (error.response?.status === 404) {
        console.log('📝 [LOAD] 404 - No buyer profile exists yet for lead:', leadDetails?.id);
        toast('ℹ️ No buyer profile found. You can create one using the edit button.', {
          duration: 3000,
          position: 'top-right'
        });
      } else if (error.response?.status === 401) {
        console.error('❌ [LOAD] Authentication error - user may need to log in again');
        toast.error('Authentication error. Please log in again.', {
          duration: 4000,
          position: 'top-right'
        });
      } else {
        toast.error('Failed to load buyer profile. Please try again.', {
          duration: 4000,
          position: 'top-right'
        });
      }
      
      setBuyerProfile(null);
      return null;
    }
  };

  const loadLeadMetrics = async () => {
    try {
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadDetails?.id}/metrics`);
      if (response.data?.data) {
        const metrics = response.data.data;
        setLeadMetrics({
          leadScore: metrics.lead_score || null,
          daysActive: metrics.days_active || null,
          responseRate: metrics.response_rate || null,
          aiConfidence: metrics.ai_confidence || null,
          loading: false
        });
      } else {
        setLeadMetrics(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading lead metrics:', error);
      setLeadMetrics(prev => ({ ...prev, loading: false }));
    }
  };

  const loadAiIntelligence = async () => {
    try {
      setAiIntelligence(prev => ({ ...prev, loading: true }));
      
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadDetails?.id}/ai-intelligence`);
      console.log('🤖 [AI INTELLIGENCE] Response:', response.data);
      
      if (response.data?.data) {
        const intelligence = response.data.data;
        
        const getBuyingIntentLabel = (score: number | null): string => {
          if (score === null) return 'Not Available';
          if (score >= 80) return 'Very High';
          if (score >= 60) return 'High';
          if (score >= 40) return 'Medium';
          if (score >= 20) return 'Low';
          return 'Very Low';
        };
        
        const getEngagementLabel = (score: number | null): string => {
          if (score === null) return 'Not Available';
          if (score >= 80) return 'Excellent';
          if (score >= 60) return 'Good';
          if (score >= 40) return 'Fair';
          return 'Poor';
        };
        
        const getConversionLabel = (score: number | null): string => {
          if (score === null) return 'Not Available';
          if (score >= 75) return 'Very High';
          if (score >= 50) return 'High';
          if (score >= 25) return 'Medium';
          return 'Low';
        };
        
        setAiIntelligence({
          buyingIntent: {
            score: intelligence.buying_intent || null,
            label: getBuyingIntentLabel(intelligence.buying_intent),
            description: intelligence.buying_intent_description || 'Analyzing lead behavior patterns'
          },
          engagementQuality: {
            score: intelligence.engagement_quality || null,
            label: getEngagementLabel(intelligence.engagement_quality),
            description: intelligence.engagement_description || 'Evaluating interaction quality'
          },
          conversionProbability: {
            score: intelligence.conversion_probability || null,
            label: getConversionLabel(intelligence.conversion_probability),
            description: intelligence.conversion_description || 'Calculating conversion likelihood'
          },
          loading: false
        });
      } else {
        setAiIntelligence(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading AI intelligence:', error);
      setAiIntelligence(prev => ({ ...prev, loading: false }));
    }
  };

  const loadRecommendedActions = async () => {
    try {
      setRecommendedActions({ actions: [], loading: true });
      
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadDetails?.id}/recommended-actions`);
      console.log('🎯 [RECOMMENDED ACTIONS] Response:', response.data);
      
      if (response.data?.data?.actions) {
        const sortedActions = response.data.data.actions.sort((a: any, b: any) => {
          const priorityOrder: { [key: string]: number } = {
            'URGENT': 0,
            'HIGH': 1,
            'MEDIUM': 2,
            'LOW': 3
          };
          
          const priorityA = priorityOrder[a.priority] ?? 4;
          const priorityB = priorityOrder[b.priority] ?? 4;
          
          return priorityA - priorityB;
        });
        
        setRecommendedActions({
          actions: sortedActions,
          loading: false
        });
      } else {
        setRecommendedActions({ actions: [], loading: false });
      }
    } catch (error) {
      console.error('Error loading recommended actions:', error);
      setRecommendedActions({ actions: [], loading: false });
    }
  };

  const loadKeyInsights = async () => {
    try {
      setLoadingInsights(true);
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadDetails?.id}/insights`);
      if (response.data?.data) {
        setKeyInsights(response.data.data.insights || []);
      } else {
        const mockInsights: KeyInsightProps[] = [
          { icon: 'faHome', type: 'property', text: `Looking for ${leadDetails?.lead_type || 'property'} in ${leadDetails?.lead_location || 'preferred area'}` },
          { icon: 'faDollarSign', type: 'budget', text: `Budget: ${leadDetails?.lead_budget || 'Not specified'}` },
          { icon: 'faClock', type: 'timeline', text: `Timeline: ${leadDetails?.lead_timeframe || 'Flexible'}` }
        ];
        setKeyInsights(mockInsights);
      }
    } catch (error) {
      console.error('Error loading key insights:', error);
      const fallbackInsights: KeyInsightProps[] = [
        { icon: 'faHome', type: 'property', text: 'Property preferences being analyzed' },
        { icon: 'faDollarSign', type: 'budget', text: 'Budget analysis in progress' },
        { icon: 'faClock', type: 'timeline', text: 'Timeline assessment pending' }
      ];
      setKeyInsights(fallbackInsights);
    } finally {
      setLoadingInsights(false);
    }
  };

  const loadRecentCommunications = async () => {
    try {
      setLoadingCommunications(true);
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadDetails?.id}/communications`);
      if (response.data?.data) {
        const communications = response.data.data.slice(0, 5).map((comm: any) => ({
          id: comm.id,
          type: comm.type || 'email',
          title: comm.subject || 'Communication',
          description: comm.snippet || comm.content || 'No preview available',
          timestamp: comm.created_at || new Date().toISOString(),
          timeAgo: formatTimeAgo(new Date(comm.created_at || Date.now())),
          color: getInteractionColor(comm.type || 'email')
        }));
        setRecentCommunications(communications);
      } else {
        setRecentCommunications([]);
      }
    } catch (error) {
      console.error('Error loading recent communications:', error);
      setRecentCommunications([]);
    } finally {
      setLoadingCommunications(false);
    }
  };

  const loadPropertyPreferences = async () => {
    try {
      console.log('🏠 [PROP PREF] Starting to load property preferences');
      console.log('🏠 [PROP PREF] Current buyerProfile:', buyerProfile);
      
      setLoadingPreferences(true);
      
      if (!buyerProfile || !buyerProfile.id) {
        console.log('⚠️ [PROP PREF] No buyer profile or ID available yet');
        setPropertyPreferences([]);
        return;
      }
      
      const preferences: PropertyPreferenceProps[] = [];
      const profilePrefs = buyerProfile.property_preferences || {};
      
      if (profilePrefs.property_type && profilePrefs.property_type !== '') {
        preferences.push({
          key: 'property_type',
          label: 'Property Type',
          value: profilePrefs.property_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          badge_color: '#3b82f6',
          description: 'Property type preference'
        });
      }
      
      if (profilePrefs.bedrooms || profilePrefs.bathrooms) {
        const beds = profilePrefs.bedrooms || 'Any';
        const baths = profilePrefs.bathrooms || 'Any';
        preferences.push({
          key: 'size',
          label: 'Size',
          value: `${beds} bed, ${baths} bath`,
          badge_color: '#8b5cf6',
          description: 'Bedroom and bathroom preferences'
        });
      }
      
      if (profilePrefs.budget_min || profilePrefs.budget_max) {
        const min = profilePrefs.budget_min ? `$${profilePrefs.budget_min.toLocaleString()}` : 'No min';
        const max = profilePrefs.budget_max ? `$${profilePrefs.budget_max.toLocaleString()}` : 'No max';
        preferences.push({
          key: 'budget',
          label: 'Budget',
          value: min === 'No min' && max === 'No max' ? 'Flexible' : `${min} - ${max}`,
          badge_color: '#10b981',
          description: 'Budget range'
        });
      }
      
      if (profilePrefs.square_feet_min || profilePrefs.square_feet_max) {
        const minSqft = profilePrefs.square_feet_min || 0;
        const maxSqft = profilePrefs.square_feet_max || 0;
        preferences.push({
          key: 'square_footage',
          label: 'Square Footage',
          value: `${minSqft.toLocaleString()} - ${maxSqft.toLocaleString()} sq ft`,
          badge_color: '#f59e0b',
          description: 'Square footage preferences'
        });
      }
      
      const locationPrefs = buyerProfile.location_preferences || {};
      if (locationPrefs.preferred_cities && locationPrefs.preferred_cities.length > 0) {
        preferences.push({
          key: 'location',
          label: 'Preferred Areas',
          value: locationPrefs.preferred_cities.join(', '),
          badge_color: '#ef4444',
          description: 'Preferred locations'
        });
      }
      
      console.log('✅ [PROP PREF] Generated preferences:', preferences);
      setPropertyPreferences(preferences);
      
    } catch (error) {
      console.error('❌ [PROP PREF] Error loading property preferences:', error);
      setPropertyPreferences([]);
    } finally {
      setLoadingPreferences(false);
    }
  };

  const loadMarketIntelligence = async () => {
    try {
      setLoadingMarket(true);
      
      const buyerPrefs = buyerProfile?.location_preferences || {};
      const preferredCities = buyerPrefs.preferred_cities || [];
      const primaryLocation = preferredCities[0] || leadDetails?.lead_location || '';
      const bedroomCount = buyerProfile?.property_preferences?.bedrooms || 3;
      
      const marketStats: MarketStatProps[] = [];
      
      if (primaryLocation && primaryLocation !== '') {
        const avgDaysOnMarket = calculateDaysOnMarket(primaryLocation, bedroomCount);
        marketStats.push({
          label: 'Avg. Days on Market',
          value: `${avgDaysOnMarket} days`,
          description: `For ${bedroomCount}BR in ${primaryLocation}`,
          color: avgDaysOnMarket < 20 ? '#10b981' : avgDaysOnMarket < 30 ? '#f59e0b' : '#ef4444'
        });
        
        const priceTrend = calculatePriceTrend(primaryLocation);
        marketStats.push({
          label: 'Price Trend',
          value: priceTrend > 0 ? `+${priceTrend.toFixed(1)}%` : `${priceTrend.toFixed(1)}%`,
          description: 'Year-over-year change',
          color: priceTrend > 0 ? '#10b981' : '#ef4444'
        });
        
        const inventoryLevel = calculateInventoryLevel(primaryLocation);
        marketStats.push({
          label: 'Inventory Level',
          value: inventoryLevel.level,
          description: inventoryLevel.description,
          color: inventoryLevel.color
        });
        
        const competition = calculateCompetition(primaryLocation);
        marketStats.push({
          label: 'Competition',
          value: competition.level,
          description: competition.description,
          color: competition.color
        });
      }
      
      if (marketStats.length === 0) {
        marketStats.push(
          { label: 'Avg. Days on Market', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Price Trend', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Inventory Level', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Competition', value: 'Not available', description: 'No location data recorded', color: '#6b7280' }
        );
      }
      
      setMarketIntelligence(marketStats);
    } catch (error) {
      console.error('Error loading market intelligence:', error);
      setMarketIntelligence([
        { label: 'Avg. Days on Market', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Price Trend', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Inventory Level', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Competition', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' }
      ]);
    } finally {
      setLoadingMarket(false);
    }
  };

  // Helper functions for data processing
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return `${Math.floor(diffInDays / 7)} weeks ago`;
  };

  // Removed unused formatInteractionTitle function

  // Modal handling functions
  const openEditModal = () => {
    console.log('🔍 openEditModal called!');
    console.log('🔍 Current buyerProfile:', buyerProfile);
    
    const profilePrefs = buyerProfile?.property_preferences || {};
    const locationPrefs = buyerProfile?.location_preferences || {};
    setEditFormData({
      bedrooms: profilePrefs.bedrooms?.toString() || '',
      bathrooms: profilePrefs.bathrooms?.toString() || '',
      budget_min: profilePrefs.budget_min?.toString() || '',
      budget_max: profilePrefs.budget_max?.toString() || '',
      property_type: profilePrefs.property_type || '',
      timeline: buyerProfile?.buying_timeframe || '',
      additional_notes: buyerProfile?.lifestyle_preferences?.deal_breakers?.join(', ') || '',
      modalTab: 'manual',
      aiMessage: '',
      pre_approved: 'false',
      pre_approval_amount: '',
      first_time_buyer: 'false',
      buyer_stage: '',
      motivation: '',
      preferred_cities: locationPrefs.preferred_cities?.join(', ') || '',
      square_feet_min: profilePrefs.square_feet_min?.toString() || '',
      square_feet_max: profilePrefs.square_feet_max?.toString() || '',
      desired_move_in_date: buyerProfile?.desired_move_in_date || '',
      max_commute_time: buyerProfile?.max_commute_time?.toString() || '',
      work_location: buyerProfile?.work_location || '',
    });
    
    console.log('🔍 Setting showEditModal to true');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  // Seller Modal handling functions
  const openSellerModal = () => {
    // Debug alert removed
    console.log('🔧 [SELLER MODAL] Opening seller modal...');
    
    setSellerFormData({
      modalTab: 'manual',
      property_address: sellerProfile?.property_address || '',
      property_city: sellerProfile?.property_city || '',
      property_state: sellerProfile?.property_state || '',
      property_zip: sellerProfile?.property_zip || '',
      property_type: sellerProfile?.property_type || '',
      bedrooms: sellerProfile?.bedrooms?.toString() || '',
      bathrooms: sellerProfile?.bathrooms?.toString() || '',
      square_footage: sellerProfile?.square_footage?.toString() || '',
      year_built: sellerProfile?.year_built?.toString() || '',
      estimated_value: sellerProfile?.estimated_value?.toString() || '',
      desired_price: sellerProfile?.desired_price?.toString() || '',
      minimum_price: sellerProfile?.minimum_price?.toString() || '',
      selling_timeframe: sellerProfile?.selling_timeframe || '',
      motivation_level: sellerProfile?.motivation_level || 'medium',
      seller_stage: sellerProfile?.seller_stage || 'exploring',
      property_condition: sellerProfile?.property_condition || '',
      recent_improvements: sellerProfile?.recent_improvements || '',
      why_selling: sellerProfile?.why_selling || ''
    });
    
    setSellerModalMode('welcome'); // Reset to welcome screen
    setShowSellerModal(true);
    console.log('🔧 [SELLER MODAL] Modal should now be visible!');
    // Debug alert removed
  };

  const closeSellerModal = () => {
    setShowSellerModal(false);
  };

  const saveBuyerPreferences = async () => {
    setSavingProfile(true);
    try {
      console.log('=== BUYER PROFILE SAVE PROCESS ===');
      console.log('Edit form data:', editFormData);

      // Helper functions for data parsing
      const safeParseInt = (value: string) => {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      const safeParseFloat = (value: string) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      const nullifyEmpty = (value: string) => {
        return value && value.trim() !== '' ? value.trim() : null;
      };

      const mapTimeframe = (value: string) => {
        const mapping: { [key: string]: string } = {
          'immediately': 'Immediately',
          'within_30_days': 'Within 30 days',
          'within_3_months': 'Within 3 months',
          'within_6_months': 'Within 6 months',
          'within_1_year': 'Within 1 year',
          'more_than_1_year': 'More than 1 year',
          'just_looking': 'Just looking',
          'not_specified': 'Not specified'
        };
        return mapping[value] || 'Not specified';
      };

      const mapBuyerStage = (value: string) => {
        const mapping: { [key: string]: string } = {
          'research': 'Research',
          'pre_approved': 'Pre-approved',
          'actively_searching': 'Actively searching',
          'making_offers': 'Making offers',
          'under_contract': 'Under contract',
          'not_specified': 'Not specified'
        };
        return mapping[value] || 'Not specified';
      };

      // Build the payload with proper data types
      const updatePayload = {
        lead_id: leadDetails.id,
        // Text fields - ensure they're strings or null
        price_range_min: safeParseFloat(editFormData.budget_min),
        price_range_max: safeParseFloat(editFormData.budget_max),
        preferred_location: nullifyEmpty(editFormData.work_location),
        property_type: nullifyEmpty(editFormData.property_type) || 'Not specified',
        bedrooms: safeParseInt(editFormData.bedrooms),
        bathrooms: safeParseFloat(editFormData.bathrooms),
        special_requirements: nullifyEmpty(editFormData.additional_notes),
        notes: nullifyEmpty(editFormData.additional_notes),
        buying_timeframe: editFormData.timeline ? mapTimeframe(editFormData.timeline) : 'Not specified',
        // Boolean fields (tinyint(1)) - must be integers for database
        pre_approved: editFormData.pre_approved === 'true' ? 1 : 0,
        pre_approval_amount: editFormData.pre_approved === 'true' ? safeParseFloat(editFormData.pre_approval_amount) : null,
        first_time_buyer: editFormData.first_time_buyer === 'true' ? 1 : 0,
        // Enum field - must match database values
        buyer_stage: editFormData.buyer_stage ? mapBuyerStage(editFormData.buyer_stage) : 'Not specified',
        created_by: userInfo?.id,
        updated_by: userInfo?.id
      };

      console.log('Update payload prepared:', updatePayload);

      // Check if buyer profile already exists
      let existingProfile = null;
      let isUpdate = false;
      
      try {
        console.log('Checking for existing buyer profile...');
        const checkResponse = await axiosInstance.get(`/buyer-profiles/lead/${leadDetails.id}`, {
          params: { user_id: userInfo?.id }
        });
        
        if (checkResponse.data && (checkResponse.data.data || checkResponse.data.buyer_profile)) {
          existingProfile = checkResponse.data.data || checkResponse.data.buyer_profile;
          isUpdate = true;
          console.log('Found existing buyer profile:', existingProfile);
        }
      } catch (checkError: any) {
        console.log('No existing buyer profile found (expected for new profiles)');
        isUpdate = false;
      }

      let response;
      
      if (isUpdate && existingProfile) {
        // UPDATE existing profile
        console.log('Updating existing buyer profile with ID:', existingProfile.id);
        response = await axiosInstance.put(`/buyer-profiles/${existingProfile.id}`, updatePayload, {
          params: { user_id: userInfo?.id }
        });
        console.log('Update response:', response.data);
      } else {
        // CREATE new profile
        console.log('Creating new buyer profile...');
        try {
          response = await axiosInstance.post('/buyer-profiles', updatePayload, {
            params: { user_id: userInfo?.id }
          });
          console.log('Create response:', response.data);
        } catch (createError: any) {
          if (createError.response?.status === 400 && 
              createError.response?.data?.message?.includes('already exists')) {
            console.log('Profile exists but not found in check - retrying as update...');
            
            // Retry the check and update
            const retryResponse = await axiosInstance.get(`/buyer-profiles/lead/${leadDetails.id}`, {
              params: { user_id: userInfo?.id }
            });
            
            if (retryResponse.data && (retryResponse.data.data || retryResponse.data.buyer_profile)) {
              const profile = retryResponse.data.data || retryResponse.data.buyer_profile;
              console.log('Found profile on retry, updating:', profile.id);
              
              response = await axiosInstance.put(`/buyer-profiles/${profile.id}`, updatePayload, {
                params: { user_id: userInfo?.id }
              });
              console.log('Retry update response:', response.data);
            } else {
              throw createError;
            }
          } else {
            throw createError;
          }
        }
      }

      console.log('Final response:', response.data);
      console.log('=== SAVE COMPLETED SUCCESSFULLY ===');
      console.log(`✅ [SAVE] Profile ${isUpdate ? 'UPDATED' : 'CREATED'} successfully!`);

      // Show success message immediately
      toast.success(`✅ Buyer profile ${isUpdate ? 'updated' : 'created'} successfully!`, {
        duration: 4000,
        position: 'top-right'
      });

      // Close modal and refresh data
      closeEditModal();
      await loadBuyerProfile();
      
      // Trigger context refresh to update AI analysis
      await triggerContextRefresh('profile_updated', {
        action: isUpdate ? 'update' : 'create',
        profile_id: response.data?.buyer_profile?.id || existingProfile?.id,
        fields_updated: Object.keys(updatePayload)
      });
      
    } catch (error: any) {
      console.error('Error saving buyer preferences:', error);
      console.error('Error response:', error.response?.data);
      
      // Determine specific error message
      let errorMessage = 'Unknown error occurred';
      
      // Check for database trigger error
      if (error.response?.data?.code === 'P2010' || 
          error.response?.data?.meta?.code === '1442' ||
          (error.response?.data?.message && error.response.data.message.includes("Can't update table")) ||
          (error.response?.data?.error && error.response.data.error.includes("stored function/trigger")) ||
          (error.response?.data?.error && error.response.data.error.includes("prisma.$queryRawUnsafe")) ||
          (error.response?.status === 500 && error.response?.data?.message === 'Failed to update buyer profile')) {
        errorMessage = 'Database trigger conflict detected. This is a known backend issue. Your changes may have been partially saved. The profile will be reloaded to show current state.';
        console.error('❌ [SAVE ERROR] Database trigger conflict - backend fix needed');
        console.error('❌ [SAVE ERROR] Full error details:', error.response?.data);
        
        // Try to reload the profile to see current state
        setTimeout(async () => {
          try {
            await loadBuyerProfile();
            toast('ℹ️ Profile reloaded. Please check if your changes were saved.', {
              duration: 5000,
              position: 'top-right'
            });
          } catch (reloadError) {
            console.error('Failed to reload profile after trigger error');
          }
        }, 2000);
        
        // Show specific message for this error
        toast.error(`❌ ${errorMessage}`, {
          duration: 8000,
          position: 'top-right'
        });
        return; // Don't show the generic error message
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
        // Clear invalid token
        localStorage.removeItem('auth_token');
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission for this action.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Profile endpoint not found. Please check server configuration.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid data provided.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again or contact support.';
      } else if (error.response?.data?.error && typeof error.response.data.error === 'string') {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show user-friendly error message
      toast.error(`❌ Failed to save buyer preferences: ${errorMessage}`, {
        duration: 6000,
        position: 'top-right'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSellerProfile = async () => {
    setSavingSellerProfile(true);
    try {
      console.log('=== SELLER PROFILE SAVE PROCESS ===');
      console.log('Seller form data:', sellerFormData);

      // Helper functions for data parsing
      const safeParseInt = (value: string) => {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      const safeParseFloat = (value: string) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      const nullifyEmpty = (value: string) => {
        return value && value.trim() !== '' ? value.trim() : null;
      };

      // Map frontend values to database enum values
      const mapSellingTimeframe = (value: string) => {
        const mapping: Record<string, string> = {
          'immediately': 'within_1_month',
          'within_30_days': 'within_1_month', 
          'within_3_months': 'within_3_months',
          'within_6_months': 'within_6_months',
          'within_1_year': 'within_1_year',
          'just_exploring': 'flexible'
        };
        return mapping[value] || value;
      };

      // Create the seller profile payload
      const sellerPayload = {
        lead_id: leadDetails?.id || '',
        property_address: nullifyEmpty(sellerFormData.property_address),
        property_city: nullifyEmpty(sellerFormData.property_city),
        property_state: nullifyEmpty(sellerFormData.property_state),
        property_zip: nullifyEmpty(sellerFormData.property_zip),
        property_type: nullifyEmpty(sellerFormData.property_type),
        bedrooms: safeParseInt(sellerFormData.bedrooms),
        bathrooms: safeParseInt(sellerFormData.bathrooms),
        square_footage: safeParseInt(sellerFormData.square_footage),
        year_built: safeParseInt(sellerFormData.year_built),
        estimated_value: safeParseFloat(sellerFormData.estimated_value),
        desired_price: safeParseFloat(sellerFormData.desired_price),
        minimum_price: safeParseFloat(sellerFormData.minimum_price),
        selling_timeframe: nullifyEmpty(mapSellingTimeframe(sellerFormData.selling_timeframe)),
        motivation_level: sellerFormData.motivation_level || 'medium',
        seller_stage: sellerFormData.seller_stage || 'considering',
        property_condition: nullifyEmpty(sellerFormData.property_condition),
        recent_improvements: !!(sellerFormData.recent_improvements && sellerFormData.recent_improvements.trim()),
        improvements_description: nullifyEmpty(sellerFormData.recent_improvements),
        reason_for_selling: nullifyEmpty(sellerFormData.why_selling),
        user_id: userInfo?.id
      };

      console.log('🚀 [SELLER SAVE] Payload prepared:', sellerPayload);

      // Debug the current seller profile state
      console.log('🔍 [SELLER SAVE] Current sellerProfile:', sellerProfile);
      console.log('🔍 [SELLER SAVE] sellerProfile.id:', sellerProfile?.id);
      console.log('🔍 [SELLER SAVE] ID starts with placeholder?', sellerProfile?.id?.startsWith('placeholder'));

      let response: any = null;
      const isUpdate = sellerProfile && sellerProfile.id && !sellerProfile.id.startsWith('placeholder');
      
      console.log('🔍 [SELLER SAVE] Should UPDATE?', isUpdate);
      console.log('🔍 [SELLER SAVE] Will use', isUpdate ? 'UPDATE' : 'CREATE', 'operation');

              if (isUpdate) {
          // UPDATE existing profile - use correct endpoint
          console.log('🔄 [SELLER SAVE] Updating existing seller profile...');
          response = await axiosInstance.put(`/sellers/${leadDetails.id}/profile`, sellerPayload, {
            params: { user_id: userInfo?.id }
          });
          console.log('✅ [SELLER SAVE] Update response:', response.data);
      } else {
        // CREATE new profile - try multiple endpoints
        console.log('Creating new seller profile...');
        
        const createEndpoints = [
          `/sellers/${leadDetails.id}/profile`,
          '/seller-profiles',
          `/seller-profiles/lead/${leadDetails.id}`,
          '/sellers/profiles'
        ];
        
        let createSuccess = false;
        let lastError = null;
        
        for (const endpoint of createEndpoints) {
          try {
            console.log(`🔍 [SELLER SAVE] Trying CREATE endpoint: ${endpoint}`);
            response = await axiosInstance.post(endpoint, sellerPayload, {
              params: { user_id: userInfo?.id }
            });
            console.log(`✅ [SELLER SAVE] SUCCESS with endpoint: ${endpoint}`, response.data);
            createSuccess = true;
            break;
          } catch (endpointError: any) {
            console.log(`⚠️ [SELLER SAVE] Failed endpoint ${endpoint}:`, endpointError.response?.status, endpointError.response?.statusText);
            lastError = endpointError;
            continue;
          }
        }
        
        if (!createSuccess) {
          console.error('❌ [SELLER SAVE] All CREATE endpoints failed');
          throw lastError;
        }
      }

      if (response) {
        console.log('Final response:', response.data);
      }
      console.log('=== SELLER SAVE COMPLETED SUCCESSFULLY ===');
      console.log(`✅ [SELLER SAVE] Profile ${isUpdate ? 'UPDATED' : 'CREATED'} successfully!`);

      // Show success message
      toast.success(`✅ Seller profile ${isUpdate ? 'updated' : 'created'} successfully!`, {
        duration: 4000,
        position: 'top-right'
      });

      // Close modal and refresh data
      closeSellerModal();
      await loadSellerProfile();

    } catch (error: any) {
      console.error('❌ [SELLER SAVE] Save failed:', error);
      console.error('❌ [SELLER SAVE] Error details:', {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data
      });

      // Extract meaningful error message
      let errorMessage = 'Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show user-friendly error message
      toast.error(`❌ Failed to save seller profile: ${errorMessage}`, {
        duration: 6000,
        position: 'top-right'
      });
    } finally {
      setSavingSellerProfile(false);
    }
  };

  const getInteractionColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'phone_call': '#3b82f6',
      'email': '#10b981',
      'text_message': '#8b5cf6',
      'initial_contact': '#f59e0b',
      'property_viewing': '#06b6d4',
      'consultation': '#84cc16',
      'follow_up': '#f97316'
    };
    return colors[type] || '#6b7280';
  };

  // Seller Profile Functions
  const loadSellerProfile = async () => {
    // Debug alert removed
    try {
      if (!leadDetails?.id) {
        console.warn('⚠️ [SELLER LOAD] No lead ID available, cannot load seller profile');
        return;
      }
      
      setLoadingSellerProfile(true);
      console.log('🏠 [SELLER LOAD] Loading seller profile for lead ID:', leadDetails.id);
      console.log('🏠 [SELLER LOAD] Lead details:', leadDetails);
      console.log('🏠 [SELLER LOAD] Lead type:', leadDetails.lead_type);
      console.log('🏠 [SELLER LOAD] Lead types ID:', leadDetails.lead_types_id);
      
      // Try multiple API endpoints for seller profile data  
      const endpoints = [
        `/sellers/${leadDetails.id}/profile`,
        `/seller-profiles/lead/${leadDetails.id}`
      ];
      
      let profileData = null;
      let successfulEndpoint = null;
      
      for (const endpoint of endpoints) {
        try {
          console.log(`🔍 [SELLER LOAD] Trying endpoint: ${endpoint}`);
          const response = await axiosInstance.get(endpoint, {
            params: { user_id: userInfo?.id }
          });
          console.log(`📥 [SELLER LOAD] Response from ${endpoint}:`, response.data);
          console.log(`📥 [SELLER LOAD] Raw response structure:`, response);
          console.log(`📥 [SELLER LOAD] Response data structure:`, response.data);
          
          // Handle different response structures
          if (response.data?.data?.seller_profile) {
            profileData = response.data.data.seller_profile;
            successfulEndpoint = endpoint;
            break;
          } else if (response.data?.seller_profile) {
            profileData = response.data.seller_profile;
            successfulEndpoint = endpoint;
            break;
          } else if (response.data?.status === 'success' && response.data?.data) {
            profileData = response.data.data;
            successfulEndpoint = endpoint;
            break;
          } else if (response.data && typeof response.data === 'object' && response.data.id) {
            profileData = response.data;
            successfulEndpoint = endpoint;
            break;
          }
        } catch (endpointError: any) {
          console.log(`⚠️ [SELLER LOAD] Endpoint ${endpoint} failed:`, endpointError.response?.status);
          continue;
        }
      }
      
      if (profileData && successfulEndpoint) {
        // Debug alert removed
        console.log(`✅ [SELLER LOAD] Found seller profile from ${successfulEndpoint}:`, profileData);
        console.log('🔍 [SELLER PROFILE DEBUG] Profile data details:', {
          property_address: profileData.property_address,
          estimated_value: profileData.estimated_value,
          selling_timeframe: profileData.selling_timeframe,
          seller_stage: profileData.seller_stage,
          property_city: profileData.property_city,
          property_state: profileData.property_state,
          bedrooms: profileData.bedrooms,
          bathrooms: profileData.bathrooms
        });
        setSellerProfile(profileData);
        
        // Check if this is a new/empty profile that needs data collection
        const isEmptyProfile = (!profileData.property_address || profileData.property_address === '') && 
                              (!profileData.estimated_value || profileData.estimated_value === 0 || profileData.estimated_value === null) && 
                              (!profileData.selling_timeframe || profileData.selling_timeframe === '') &&
                              (profileData.seller_stage === 'considering' || profileData.seller_stage === 'initial_contact');
        
        console.log('🔍 [EMPTY PROFILE CHECK] Result:', {
          isEmptyProfile,
          hasAddress: !!profileData.property_address,
          hasValue: !!profileData.estimated_value,
          hasTimeframe: !!profileData.selling_timeframe,
          stage: profileData.seller_stage
        });
        // Debug alert removed
        
        if (isEmptyProfile) {
          console.log('📝 [SELLER WORKFLOW] Detected new seller profile - opening modal for data collection');
          setIsNewSellerLead(true);
          toast('👋 Welcome! Let\'s collect some property details to get started.', { 
            duration: 5000,
            icon: 'ℹ️'
          });
          // Auto-open modal for new seller leads after a brief delay
          setTimeout(() => {
            setShowSellerModal(true);
          }, 1500);
        } else {
          setIsNewSellerLead(false);
          console.log('ℹ️ [SELLER WORKFLOW] Profile has data - showing normal interface');
          toast.success('✅ Seller profile loaded successfully');
        }
      } else {
        console.log('📝 [SELLER LOAD] No seller profile found - this is normal for new seller leads');
        
        // Create a placeholder profile with lead data for UI purposes
        const placeholderProfile: SellerProfile = {
          id: `placeholder_${leadDetails.id}`,
          lead_id: leadDetails.id,
          property_address: '',
          property_city: '',
          property_state: 'AZ',
          property_zip: '',
          property_type: 'single_family',
          bedrooms: null,
          bathrooms: null,
          square_footage: null,
          year_built: null,
          estimated_value: null,
          desired_price: null,
          minimum_price: null,
          selling_timeframe: '',
          motivation_level: 'medium',
          seller_stage: 'initial_contact',
          property_condition: 'good',
          recent_improvements: null,
          why_selling: null
        };
        
        setSellerProfile(placeholderProfile);
        console.log('📋 [SELLER LOAD] Created placeholder profile for new seller lead');
      }
    } catch (error: any) {
      console.error('❌ [SELLER LOAD] Failed to load seller profile:', error);
      console.error('❌ [SELLER LOAD] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Create placeholder profile for new sellers
      const placeholderProfile: SellerProfile = {
        id: `placeholder_${leadDetails.id}`,
        lead_id: leadDetails.id,
        property_address: '',
        property_city: '',
        property_state: 'AZ',
        property_zip: '',
        property_type: 'single_family',
        bedrooms: null,
        bathrooms: null,
        square_footage: null,
        year_built: null,
        estimated_value: null,
        desired_price: null,
        minimum_price: null,
        selling_timeframe: '',
        motivation_level: 'medium',
        seller_stage: 'initial_contact',
        property_condition: 'good',
        recent_improvements: null,
        why_selling: null
      };
      
      setSellerProfile(placeholderProfile);
      
      if (error.response?.status === 404) {
        toast('ℹ️ No seller profile found. Ready to create new seller profile.', {
          duration: 4000,
          position: 'top-right'
        });
      } else {
        toast.error('Failed to load seller profile. Using default template.', {
          duration: 4000,
          position: 'top-right'
        });
      }
    } finally {
      setLoadingSellerProfile(false);
    }
  };

  const loadPropertyAnalysis = async () => {
    try {
      if (!leadDetails?.id) {
        console.warn('⚠️ [PROPERTY ANALYSIS] No lead ID available');
        return;
      }
      
      setLoadingPropertyAnalysis(true);
      console.log('🧠 [PROPERTY ANALYSIS] Loading AI property analysis for lead:', leadDetails.id);
      
      const response = await llmAxiosInstance.get(`/seller/${leadDetails.id}/ai-insights`, {
        params: { 
          user_id: userInfo?.id,
          analysis_type: 'property_valuation'
        }
      });
      
      if (response.data?.status === 'success' && response.data?.analysis) {
        setAiPropertyAnalysis(response.data.analysis);
        console.log('✅ [PROPERTY ANALYSIS] Analysis loaded successfully');
      } else {
        console.log('📝 [PROPERTY ANALYSIS] No analysis data available - staying blank until real data uploaded');
        // Leave completely blank - no sample data
        setAiPropertyAnalysis(null);
      }
    } catch (error: any) {
      console.error('❌ [PROPERTY ANALYSIS] Failed to load analysis:', error);
      console.error('❌ [PROPERTY ANALYSIS] Error details:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url
      });
      
      // No fallback data - keep it blank until user provides real data
      setAiPropertyAnalysis(null);
      
      if (error.response?.status === 404) {
        console.log('ℹ️ [PROPERTY ANALYSIS] No analysis available - awaiting user data upload');
      } else {
        toast.error('Failed to load AI analysis. Please upload property documents.', {
          duration: 4000,
          position: 'top-right'
        });
      }
    } finally {
      setLoadingPropertyAnalysis(false);
    }
  };

  const handlePDFUpload = async (file: File) => {
    try {
      // Validation
      if (file.size > 100 * 1024 * 1024) {
        throw new Error('File too large. Maximum 100MB allowed.');
      }
      if (!file.type.includes('pdf')) {
        throw new Error('Please upload PDF files only.');
      }
      if (file.name.includes('../')) {
        throw new Error('Invalid file name.');
      }
      
      setUploadingPDF(true);
      console.log('📄 [PDF UPLOAD] Starting upload:', file.name);
      
      // 1. Try PDF analysis first, then fallback to standard attachment
      let response;
      const formData = new FormData();
      
      console.log('📄 [PDF UPLOAD] Starting upload:', {
        fileName: file.name,
        fileSize: file.size,
        leadId: leadDetails?.id,
        userId: userInfo?.id
      });
      
      try {
        // Try PDF analysis endpoint first
        formData.append('pdf', file);
        formData.append('lead_id', leadDetails?.id || '');
        formData.append('user_id', String(userInfo?.id || ''));
        
        console.log('📄 [PDF UPLOAD] Attempting analysis via /sellers/pdf/analyze');
        response = await axiosInstance.post('/sellers/pdf/analyze', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000, // 2 minutes for large PDF processing
        });
      } catch (analysisError: any) {
        console.warn('⚠️ [PDF UPLOAD] Analysis endpoint failed, using standard attachment process:', analysisError.response?.status);
        
        // Fallback to standard lead attachment process (following LeadDetailsAttachments pattern)
        const attachmentFormData = new FormData();
        attachmentFormData.append('leadAttachment', file); // Use standard parameter name
        
        console.log('🔄 [PDF UPLOAD] Fallback to /leads/:id/attachments');
        response = await axiosInstance.post(`/leads/${leadDetails?.id}/attachments`, attachmentFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Handle standard attachment response
        if (response.data?.success && response.data?.data?.attachments) {
          console.log('✅ [PDF UPLOAD] Attachment saved successfully via fallback:', response.data);
          
          // Find the uploaded attachment
          const uploadedAttachment = response.data.data.attachments.find((a: any) => a.attachment_title === file.name);
          
                     const newReport: PDFReport = {
             id: uploadedAttachment?.id || Date.now().toString(),
             lead_id: leadDetails?.id || '',
             file_name: file.name,
             file_path: uploadedAttachment?.attachment_link || '',
             analysis_status: 'pending', // File uploaded, analysis pending
             ai_analysis: null,
             uploaded_at: new Date().toISOString()
           };
          
          setUploadedReports(prev => [...prev, newReport]);
          
          // Success message indicating the file was saved but analysis wasn't available
          toast.success('📄 PDF uploaded successfully to S3! Analysis not available yet.', {
            duration: 5000,
            position: 'top-right'
          });
          
          console.log('📄 [PDF UPLOAD] File details:', {
            fileName: file.name,
            fileSize: file.size,
            s3Link: uploadedAttachment?.attachment_link,
            bucketPath: `${userInfo?.id}/lead_attachments/${leadDetails?.id}/`,
            uploadedAt: new Date().toISOString()
          });
          
          return;
        } else {
          console.error('❌ [PDF UPLOAD] Unexpected attachment response format:', response.data);
          throw new Error('Attachment upload succeeded but response format was unexpected');
        }
      }
      
      console.log('📄 [PDF UPLOAD] Analysis response:', response.data);
      
      if (response.data?.status === 'success') {
        // 3. Update uploaded reports list
        const newReport: PDFReport = {
          id: response.data.report_id || Date.now().toString(),
          lead_id: leadDetails?.id || '',
          file_name: file.name,
          file_path: response.data.file_path || '',
          analysis_status: 'completed',
          ai_analysis: response.data.analysis,
          uploaded_at: new Date().toISOString()
        };
        
        setUploadedReports(prev => [...prev, newReport]);
        
        // 4. Update property analysis if available
        if (response.data.analysis) {
          // Process the analysis data according to PDF_EXTRACTION_STRATEGY.md structure
          const processedAnalysis: PropertyAnalysis = {
            valuationConfidence: response.data.analysis.market_analysis?.pricing_insights?.confidence_level === 'High' ? 90 : 
                                response.data.analysis.market_analysis?.pricing_insights?.confidence_level === 'Medium' ? 75 : 60,
            marketPosition: response.data.analysis.market_analysis?.overall_stats?.market_activity || 'Analyzing...',
            pricingStrategy: response.data.analysis.market_analysis?.pricing_insights?.optimal_price ? 
                           `Optimal: $${response.data.analysis.market_analysis.pricing_insights.optimal_price.toLocaleString()}` : 
                           'Competitive pricing recommended',
            timeToSell: response.data.analysis.market_analysis?.overall_stats?.avg_days_to_sell ? 
                       `${response.data.analysis.market_analysis.overall_stats.avg_days_to_sell} days` : 
                       '45-75 days estimated',
            recommendations: response.data.analysis.seller_insights?.recommendations || [
              'Property analysis completed',
              'Review pricing strategy',
              'Consider market positioning'
            ],
            comparableProperties: response.data.analysis.comparables || [],
            marketStats: response.data.analysis.market_stats || null,
            subjectProperty: response.data.analysis.subject_property || null
          };
          
          setAiPropertyAnalysis(processedAnalysis);
          console.log('✅ [PDF ANALYSIS] Processed analysis data:', processedAnalysis);
        }
        
        // 5. Trigger context refresh
        await triggerContextRefresh('seller_pdf_analysis', response.data.analysis);
        
        toast.success('📄 PDF analyzed successfully!', {
          duration: 4000,
          position: 'top-right'
        });
      } else {
        console.warn('⚠️ [PDF UPLOAD] Analysis response not successful:', response.data);
        throw new Error('PDF analysis failed - server response indicates failure');
      }
    } catch (error: any) {
      console.error('❌ [PDF UPLOAD] Error:', error);
      console.error('❌ [PDF UPLOAD] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      
      let errorMessage = 'PDF analysis failed. Please try again.';
      
      // Handle validation errors
      if (error.message.includes('size')) {
        errorMessage = 'File too large. Maximum 100MB allowed.';
      } else if (error.message.includes('type')) {
        errorMessage = 'Please upload PDF files only.';
      } else if (error.message.includes('name')) {
        errorMessage = 'Invalid file name.';
      } 
      // Handle HTTP response errors
      else if (error.response?.status === 500) {
        errorMessage = 'Server error during PDF processing. Attempting to save as attachment...';
        // Log additional server error details
        console.error('❌ [PDF UPLOAD] 500 Server Error - Check backend logs for:', {
          endpoint: '/sellers/pdf/analyze',
          leadId: leadDetails?.id,
          userId: userInfo?.id,
          fileName: file.name,
          serverResponse: error.response?.data
        });
        
      } else if (error.response?.status === 404) {
        errorMessage = 'PDF analysis service not found. Please contact support.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large for server processing. Maximum 100MB allowed.';
      } else if (error.response?.status === 422) {
        errorMessage = error.response?.data?.message || 'Invalid file format or corrupted PDF.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please try with a smaller file or check your connection.';
      }
      
      // Follow LeadDetailsAttachments error handling pattern
      toast.error(error.response?.data?.message || errorMessage);
    } finally {
      setUploadingPDF(false);
    }
  };

  const calculateDaysOnMarket = (location: string, bedrooms: number): number => {
    const baseDOM = location.toLowerCase().includes('downtown') ? 15 : 
                   location.toLowerCase().includes('scottsdale') ? 22 : 18;
    const bedroomAdjustment = bedrooms > 4 ? 5 : bedrooms < 3 ? -3 : 0;
    return Math.max(10, baseDOM + bedroomAdjustment + Math.floor(Math.random() * 8 - 4));
  };

  const calculatePriceTrend = (location: string): number => {
    const baseTrend = location.toLowerCase().includes('downtown') ? 4.2 : 
                     location.toLowerCase().includes('scottsdale') ? 2.8 : 3.2;
    return baseTrend + (Math.random() * 2 - 1);
  };

  const calculateInventoryLevel = (location: string) => {
    const levels = [
      { level: 'Low', description: "Buyer's market", color: '#ef4444' },
      { level: 'Moderate', description: 'Balanced market', color: '#f59e0b' },
      { level: 'High', description: "Seller's market", color: '#10b981' }
    ];
    
    const index = location.toLowerCase().includes('downtown') ? 0 : 
                 location.toLowerCase().includes('scottsdale') ? 1 : 
                 Math.floor(Math.random() * 3);
    return levels[index];
  };

  const calculateCompetition = (location: string) => {
    const baseCompetition = location.toLowerCase().includes('downtown') ? 'High' : 
                           location.toLowerCase().includes('scottsdale') ? 'Medium' : 'Medium';
    
    const descriptions: { [key: string]: string } = {
      'High': '4-6 offers typical',
      'Medium': '2-4 offers typical',
      'Low': '1-2 offers typical'
    };
    
    const colors: { [key: string]: string } = {
      'High': '#ef4444',
      'Medium': '#f59e0b',
      'Low': '#10b981'
    };
    
    return {
      level: baseCompetition,
      description: descriptions[baseCompetition],
      color: colors[baseCompetition]
    };
  };

  const renderInsightsOverview = () => (
    <div className="ai-intelligence-center">
      {/* AI Intelligence Header Dashboard */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Row>
          <Col>
            <div className="d-flex align-items-center mb-3">
              <div style={{ position: 'relative', marginRight: '16px' }}>
                <FontAwesomeIcon 
                  icon={faBolt} 
                  style={{ 
                    color: '#00d4ff', 
                    fontSize: '32px',
                    filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                  }} 
                />
                </div>
              <div>
                <h4 style={{ 
                  color: '#1f2937', 
                  fontWeight: 600, 
                  marginBottom: '4px',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.2)' 
                }}>
                  AI Intelligence Center
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Comprehensive lead analysis for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                </p>
                </div>
      </div>

            {/* Key Metrics Row */}
            <Row className="g-2">
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} className="text-success mb-2" size="lg" />
                  <div className="fw-bold text-success" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.leadScore !== null ? leadMetrics.leadScore : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Lead Score</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faClock} className="text-primary mb-2" size="lg" />
                  <div className="fw-bold text-primary" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.daysActive !== null ? leadMetrics.daysActive : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Days Active</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-warning mb-2" size="lg" />
                  <div className="fw-bold text-warning" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.responseRate !== null ? `${leadMetrics.responseRate}%` : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Response Rate</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="text-info mb-2" size="lg" />
                  <div className="fw-bold text-info" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.aiConfidence !== null ? `${leadMetrics.aiConfidence}%` : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">AI Confidence</small>
                </div>
              </Col>
              <Col className="d-flex">
                {(() => {
                  const highPriorityActions = recommendedActions.actions.filter(action => 
                    action.priority === 'URGENT' || action.priority === 'HIGH'
                  );
                  const urgentActions = recommendedActions.actions.filter(action => action.priority === 'URGENT');
                  
                  if (urgentActions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>URGENT</div>
                        <small>{urgentActions.length} urgent action{urgentActions.length > 1 ? 's' : ''} required</small>
                      </div>
                    );
                  } else if (highPriorityActions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.8) 0%, rgba(217, 119, 6, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>HIGH PRIORITY</div>
                        <small>{highPriorityActions.length} action{highPriorityActions.length > 1 ? 's' : ''} needed</small>
                      </div>
                    );
                  } else if (recommendedActions.actions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>NORMAL</div>
                        <small>{recommendedActions.actions.length} action{recommendedActions.actions.length > 1 ? 's' : ''} available</small>
                      </div>
                    );
                  } else {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.8) 0%, rgba(75, 85, 99, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(107, 114, 128, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>ALL CLEAR</div>
                        <small>No immediate actions required</small>
                      </div>
                    );
                  }
                })()}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Buyer Preferences Section */}
      {renderBuyerPreferences()}

      {/* AI Analysis Panels */}
      <Row className="g-4">
        {/* Lead Intelligence Panel */}
        <Col lg={6}>
          <div 
            className="intelligence-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={faLightbulb} 
                  className="me-2"
                  style={{ color: '#00d4ff', fontSize: '20px' }}
                />
                <h5 className="mb-0 fw-semibold">AI Lead Intelligence</h5>
              </div>
              
              {/* Real-time notification indicator */}
              <div className="d-flex align-items-center">
                {realTimeNotifications.hasContextUpdate && (
                  <Badge 
                    bg="success" 
                    className="me-2 pulse-animation"
                    style={{ 
                      fontSize: '10px',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    🔄 UPDATED
                  </Badge>
                )}
                {realTimeNotifications.hasNewRecommendations && (
                  <Badge 
                    bg="warning" 
                    className="me-2 pulse-animation"
                    style={{ 
                      fontSize: '10px',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    🔔 NEW INSIGHTS
                  </Badge>
                )}
                {realTimeNotifications.lastUpdateTime && (
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    Last updated: {realTimeNotifications.lastUpdateTime.toLocaleTimeString()}
                  </small>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-2"
                  onClick={() => triggerContextRefresh('manual_refresh')}
                  style={{ 
                    fontSize: '11px',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                >
                  <FontAwesomeIcon icon={faRefresh} className="me-1" />
                  Refresh
                </Button>
              </div>
          </div>
            
            <div className="intelligence-content">
              {aiIntelligence.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading AI Intelligence...</span>
                </div>
              ) : (
                <>
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Buying Intent</span>
                      <Badge bg={aiIntelligence.buyingIntent.label === 'Very High' ? 'success' : 
                                 aiIntelligence.buyingIntent.label === 'High' ? 'primary' : 
                                 aiIntelligence.buyingIntent.label === 'Medium' ? 'warning' : 'secondary'}>
                        {aiIntelligence.buyingIntent.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.buyingIntent.label === 'Very High' ? 'bg-success' : 
                                                   aiIntelligence.buyingIntent.label === 'High' ? 'bg-primary' : 
                                                   aiIntelligence.buyingIntent.label === 'Medium' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.buyingIntent.score !== null ? `${aiIntelligence.buyingIntent.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.buyingIntent.description}
                    </small>
                  </div>
                  
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Engagement Quality</span>
                      <Badge bg={aiIntelligence.engagementQuality.label === 'Excellent' ? 'success' : 
                                 aiIntelligence.engagementQuality.label === 'Good' ? 'primary' : 
                                 aiIntelligence.engagementQuality.label === 'Fair' ? 'warning' : 'secondary'}>
                        {aiIntelligence.engagementQuality.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.engagementQuality.label === 'Excellent' ? 'bg-success' : 
                                                   aiIntelligence.engagementQuality.label === 'Good' ? 'bg-primary' : 
                                                   aiIntelligence.engagementQuality.label === 'Fair' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.engagementQuality.score !== null ? `${aiIntelligence.engagementQuality.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.engagementQuality.description}
                    </small>
                  </div>
                  
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Conversion Probability</span>
                      <Badge bg={aiIntelligence.conversionProbability.label === 'Very High' ? 'success' : 
                                 aiIntelligence.conversionProbability.label === 'High' ? 'primary' : 
                                 aiIntelligence.conversionProbability.label === 'Medium' ? 'warning' : 'secondary'}>
                        {aiIntelligence.conversionProbability.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.conversionProbability.label === 'Very High' ? 'bg-success' : 
                                                   aiIntelligence.conversionProbability.label === 'High' ? 'bg-primary' : 
                                                   aiIntelligence.conversionProbability.label === 'Medium' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.conversionProbability.score !== null ? `${aiIntelligence.conversionProbability.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.conversionProbability.description}
                    </small>
                  </div>
                </>
              )}
              
              <hr className="my-3" />
              
              <div className="key-insights">
                <h6 className="fw-semibold mb-2">Key Insights</h6>
                {loadingInsights ? (
                  <div className="text-center py-2">
                    <small className="text-muted">Loading insights...</small>
                  </div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {keyInsights.map((insight, index) => (
                      <li key={index} className="mb-2">
                        <FontAwesomeIcon 
                          icon={insight.icon === 'faHome' ? faHome : insight.icon === 'faDollarSign' ? faDollarSign : faClock} 
                          className={`me-2 ${insight.type === 'property' ? 'text-primary' : insight.type === 'budget' ? 'text-success' : 'text-warning'}`} 
                          size="sm" 
                        />
                        <small>{insight.text}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Col>
        
        {/* Action Center Panel */}
        <Col lg={6}>
          <div 
            className="action-center"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon 
                icon={faTasks} 
                className="me-2"
                style={{ color: '#10b981', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Recommended Actions</h5>
            </div>
            
            <div className="action-content">
              {recommendedActions.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading Recommendations...</span>
                </div>
              ) : recommendedActions.actions.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="2x" />
                  <p>No immediate actions required</p>
                </div>
              ) : (
                recommendedActions.actions.map((action) => {
                  const getActionStyles = (priority: string) => {
                    switch (priority) {
                      case 'URGENT':
                        return {
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          badgeColor: 'danger',
                          textColor: 'text-danger',
                          variant: 'outline-danger'
                        };
                      case 'HIGH':
                        return {
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          badgeColor: 'warning',
                          textColor: 'text-warning',
                          variant: 'outline-warning'
                        };
                      case 'MEDIUM':
                        return {
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          badgeColor: 'primary',
                          textColor: 'text-primary',
                          variant: 'outline-primary'
                        };
                      default:
                        return {
                          background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)',
                          border: '1px solid rgba(107, 114, 128, 0.2)',
                          badgeColor: 'secondary',
                          textColor: 'text-secondary',
                          variant: 'outline-secondary'
                        };
                    }
                  };

                  const styles = getActionStyles(action.priority);
                  
                  return (
                    <div key={action.id} className="action-item mb-3">
                      <div 
                        className="action-card"
                        style={{
                          background: styles.background,
                          border: styles.border,
                          borderRadius: '12px',
                          padding: '16px',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                            {action.title}
                          </h6>
                          <Badge bg={styles.badgeColor} style={{ fontSize: '10px' }}>
                            {action.priority}
                          </Badge>
                        </div>
                        <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                          {action.description}
                        </p>
                        <div className="d-flex align-items-center justify-content-end">
                          <Button
                            size="sm"
                            variant={styles.variant}
                            style={{ fontSize: '11px', padding: '4px 12px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.success(`Action marked: ${action.title}`);
                            }}
                          >
                            Take Action
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Recent Communications & Market Intelligence */}
      <Row className="g-4 mt-2">
        <Col lg={6}>
          <div 
            className="communications-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon 
                icon={faEnvelope} 
                className="me-2"
                style={{ color: '#8b5cf6', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Recent Communications</h5>
            </div>
            
            <div className="communications-content">
              {loadingCommunications ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading communications...</span>
                </div>
              ) : recentCommunications.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FontAwesomeIcon icon={faEnvelope} className="mb-2" size="2x" />
                  <p>No recent communications</p>
                </div>
              ) : (
                recentCommunications.map((comm) => (
                  <div key={comm.id} className="communication-item mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-start">
                      <div 
                        className="communication-icon me-3 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '36px',
                          height: '36px',
                          background: comm.color,
                          color: 'white'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={comm.type === 'email' ? faEnvelope : faUser} 
                          size="sm"
                        />
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <h6 className="mb-0" style={{ fontSize: '14px' }}>
                            {comm.title}
                          </h6>
                          <small className="text-muted">
                            {comm.timeAgo}
                          </small>
                        </div>
                        <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                          {comm.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Col>
        
        <Col lg={6}>
          <div 
            className="market-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon 
                icon={faChartLine} 
                className="me-2"
                style={{ color: '#f59e0b', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Market Intelligence</h5>
            </div>
            
            <div className="market-content">
              {loadingMarket ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading market data...</span>
                </div>
              ) : (
                <Row className="g-3">
                  {marketIntelligence.map((stat, index) => (
                    <Col xs={6} key={index}>
                      <div 
                        className="market-stat-card"
                        style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon 
                            icon={faChartLine} 
                            className="me-2"
                            style={{ color: stat.color, fontSize: '16px' }}
                          />
                          <small className="text-muted fw-semibold">
                            {stat.label}
                          </small>
                        </div>
                        <h5 className="mb-1" style={{ color: stat.color }}>
                          {stat.value}
                        </h5>
                        <small className="text-muted">
                          {stat.description}
                        </small>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const renderBuyerPreferences = () => {
    // Always render the container - don't check for buyerProfile
    return (
      <div className="buyer-preferences-section mb-4">
        <div 
          className="preferences-panel"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            backdropFilter: 'blur(10px)',
            padding: '24px',
            position: 'relative',
            zIndex: 1,
            pointerEvents: 'auto',
            isolation: 'isolate'
          }}
          onClick={(e) => {
            console.log('🟣 Panel clicked', e.target);
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faHome} 
                className="me-2"
                style={{ color: '#3b82f6', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Property Preferences</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => openEditModal()}
              >
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                {buyerProfile ? 'Edit Profile' : 'Create Profile'}
              </Button>
              <Badge bg="primary" style={{ fontSize: '11px' }}>
                AI-Enhanced Profile
              </Badge>
            </div>
          </div>
          
          <div className="preferences-content">
            {loadingPreferences ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Loading preferences...</span>
              </div>
            ) : propertyPreferences.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <FontAwesomeIcon icon={faHome} className="mb-2" size="2x" />
                <p>No property preferences recorded yet</p>
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🔴 Create Profile button clicked!');
                      openEditModal();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ 
                      position: 'relative', 
                      zIndex: 9999,
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                      isolation: 'isolate'
                    }}
                  >
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Create Buyer Profile
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      toast('Use SimpleLeadAI chat to build buyer profile', {
                        icon: 'ℹ️',
                        duration: 4000,
                        position: 'top-right'
                      });
                    }}
                  >
                    <span className="me-2" style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '16px' }}>K</span>
                    Build with AI
                  </Button>
                </div>
              </div>
            ) : (
              <Row className="g-3">
                {propertyPreferences.map((pref, index) => (
                  <Col xs={12} sm={6} md={4} key={index}>
                    <div 
                      className="preference-card"
                      style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon 
                          icon={pref.key === 'budget' ? faDollarSign : pref.key === 'location' ? faMapMarkerAlt : faHome} 
                          className="me-2"
                          style={{ color: pref.badge_color || '#3b82f6', fontSize: '16px' }}
                        />
                        <small className="text-muted fw-semibold">
                          {pref.label}
                        </small>
                      </div>
                      <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                        {pref.value}
                      </p>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSellerProfile = () => {
    return (
      <div className="seller-profile-section">
        {/* Seller Profile Header */}
        <div 
          className="seller-header mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faBuilding} 
                className="me-2"
                style={{ color: '#f59e0b', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Seller Profile & Property Details</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* DEBUG: Test Modal Button */}
              <Button variant="danger" size="sm" onClick={openSellerModal}>
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                TEST MODAL
              </Button>
              {loadingSellerProfile ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Badge bg="warning" style={{ fontSize: '11px' }}>
                  <FontAwesomeIcon icon={faBuilding} className="me-1" />
                  SELLER AI
                </Badge>
              )}
            </div>
          </div>
          
          {/* Property Address */}
          {sellerProfile?.property_address && (
            <div className="property-address-display mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-warning me-2" />
                <h6 className="mb-0 fw-semibold">
                  {sellerProfile.property_address}
                  {sellerProfile.property_city && (
                    <span className="text-muted ms-2">
                      {sellerProfile.property_city}, {sellerProfile.property_state} {sellerProfile.property_zip}
                    </span>
                  )}
                </h6>
              </div>
            </div>
          )}
        </div>

        {/* Property Details Cards */}
        <Row className="g-4 mb-4">
          <Col md={6}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <Card.Header>
                <FontAwesomeIcon icon={faHome} className="text-warning me-2" />
                <strong>Property Specifications</strong>
              </Card.Header>
              <Card.Body>
                {loadingSellerProfile ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading property details...</span>
                  </div>
                ) : sellerProfile && (sellerProfile.bedrooms || sellerProfile.bathrooms || sellerProfile.square_footage || sellerProfile.year_built) ? (
                  <Row className="g-3">
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Bedrooms</small>
                        <span className="fw-semibold">
                          {sellerProfile.bedrooms || 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Bathrooms</small>
                        <span className="fw-semibold">
                          {sellerProfile.bathrooms || 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Square Footage</small>
                        <span className="fw-semibold">
                          {sellerProfile.square_footage ? `${sellerProfile.square_footage.toLocaleString()} sq ft` : 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Year Built</small>
                        <span className="fw-semibold">
                          {sellerProfile.year_built || 'Not specified'}
                        </span>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon={faHome} className="mb-2" size="2x" />
                    <p className="mb-1">No property details available</p>
                    <p className="small text-muted mb-3">
                      {sellerProfile?.id?.startsWith('placeholder') ? 
                        'Ready to create seller profile for ' + (leadDetails?.lead_first_name || 'this lead') :
                        'Property details needed for AI analysis'
                      }
                    </p>
                    <Button 
                      variant="outline-warning" 
                      size="sm" 
                      onClick={openSellerModal}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add Property Details
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <Card.Header>
                <FontAwesomeIcon icon={faDollarSign} className="text-success me-2" />
                <strong>Pricing & Timeline</strong>
              </Card.Header>
              <Card.Body>
                {loadingSellerProfile ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading pricing details...</span>
                  </div>
                ) : sellerProfile && (sellerProfile.estimated_value || sellerProfile.desired_price || sellerProfile.selling_timeframe) ? (
                  <Row className="g-3">
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Estimated Value</small>
                        <span className="fw-semibold text-success">
                          {sellerProfile.estimated_value 
                            ? `$${sellerProfile.estimated_value.toLocaleString()}` 
                            : 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Desired Price</small>
                        <span className="fw-semibold text-primary">
                          {sellerProfile.desired_price 
                            ? `$${sellerProfile.desired_price.toLocaleString()}` 
                            : 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Selling Timeframe</small>
                        <span className="fw-semibold">
                          {sellerProfile.selling_timeframe || 'Not specified'}
                        </span>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div className="spec-item">
                        <small className="text-muted d-block">Motivation Level</small>
                        <Badge 
                          bg={sellerProfile.motivation_level === 'high' ? 'danger' : 
                              sellerProfile.motivation_level === 'medium' ? 'warning' : 'secondary'}
                        >
                          {sellerProfile.motivation_level || 'Medium'}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon={faDollarSign} className="mb-2" size="2x" />
                    <p className="mb-1">No pricing information available</p>
                    <p className="small text-muted mb-3">
                      Add valuation and timeline details for {leadDetails?.lead_first_name || 'seller'}
                    </p>
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      onClick={() => {
                        console.log('🔧 [DEBUG] Add Pricing Details button clicked!');
                        openSellerModal();
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add Pricing Details
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* PDF Upload & AI Analysis */}
        <Row className="g-4 mb-4">
          <Col md={6}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <Card.Header>
                <FontAwesomeIcon icon={faUpload} className="text-info me-2" />
                <strong>PDF Report Upload</strong>
              </Card.Header>
              <Card.Body>
                <div className="pdf-upload-area text-center py-4">
                  {uploadingPDF ? (
                    <div>
                      <Spinner animation="border" className="mb-3" />
                      <p>Uploading and analyzing document...</p>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon icon={faFileAlt} className="text-muted mb-3" size="3x" />
                      <p className="text-muted mb-3">
                        Upload CMA reports, appraisals, or property documents for AI analysis
                      </p>
                      <small className="text-muted d-block mb-3">
                        PDF files only, maximum 100MB
                      </small>
                      <div className="pdf-upload-controls">
                        <input
                          type="file"
                          id="pdf-upload-input"
                          accept=".pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              console.log('📄 [PDF UI] File selected:', file.name);
                              handlePDFUpload(file);
                            }
                          }}
                        />
                        <Button 
                          variant="outline-info"
                          onClick={() => {
                            console.log('📄 [PDF UI] Select PDF button clicked');
                            document.getElementById('pdf-upload-input')?.click();
                          }}
                        >
                          <FontAwesomeIcon icon={faUpload} className="me-1" />
                          Select PDF Files
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Uploaded Reports List */}
                {uploadedReports.length > 0 && (
                  <div className="uploaded-reports mt-3">
                    <h6 className="fw-semibold mb-2">Uploaded Reports</h6>
                    {uploadedReports.map((report, index) => (
                      <div key={index} className="report-item d-flex align-items-center justify-content-between p-2 mb-2 bg-light rounded">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faFileAlt} className="text-info me-2" />
                          <div>
                            <span className="d-block">{report.file_name}</span>
                            <small className="text-muted">{new Date(report.uploaded_at).toLocaleString()}</small>
                          </div>
                        </div>
                                                 <Badge bg={
                           report.analysis_status === 'completed' ? 'success' : 
                           report.analysis_status === 'pending' ? 'primary' : 
                           report.analysis_status === 'processing' ? 'warning' : 'secondary'
                         }>
                           {report.analysis_status === 'completed' ? 'Analyzed' :
                            report.analysis_status === 'pending' ? 'Uploaded' :
                            report.analysis_status === 'processing' ? 'Processing' : 'Failed'}
                         </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              <Card.Header>
                <FontAwesomeIcon icon={faBolt} className="text-primary me-2" />
                <strong>AI Property Analysis</strong>
              </Card.Header>
              <Card.Body>
                {loadingPropertyAnalysis ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading AI analysis...</span>
                  </div>
                ) : aiPropertyAnalysis ? (
                  <div className="ai-analysis-content">
                    <Row className="g-3 mb-3">
                      <Col xs={6}>
                        <div className="metric-item text-center">
                          <div className="metric-value text-primary fw-bold" style={{ fontSize: '24px' }}>
                            {aiPropertyAnalysis.valuationConfidence}%
                          </div>
                          <small className="text-muted">Valuation Confidence</small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="metric-item text-center">
                          <div className="metric-value text-success fw-bold" style={{ fontSize: '16px' }}>
                            {aiPropertyAnalysis.timeToSell}
                          </div>
                          <small className="text-muted">Est. Time to Sell</small>
                        </div>
                      </Col>
                    </Row>
                    
                    <div className="analysis-details">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-medium">Market Position</span>
                        <Badge bg="info">{aiPropertyAnalysis.marketPosition}</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="fw-medium">Pricing Strategy</span>
                        <Badge bg="warning">{aiPropertyAnalysis.pricingStrategy}</Badge>
                      </div>
                      
                      {/* Subject Property Details */}
                      {aiPropertyAnalysis.subjectProperty && (
                        <div className="subject-property mb-3">
                          <h6 className="fw-semibold mb-2">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-danger me-2" />
                            Subject Property
                          </h6>
                          <div className="property-details p-2 bg-light rounded">
                            <div className="fw-medium mb-1">{aiPropertyAnalysis.subjectProperty.address}</div>
                            <div className="text-muted mb-1">
                              {aiPropertyAnalysis.subjectProperty.city}, {aiPropertyAnalysis.subjectProperty.state} {aiPropertyAnalysis.subjectProperty.zip}
                            </div>
                            <div className="property-specs d-flex justify-content-between">
                              <span>{aiPropertyAnalysis.subjectProperty.beds}bd/{aiPropertyAnalysis.subjectProperty.baths}ba</span>
                              {aiPropertyAnalysis.subjectProperty.sqft && (
                                <span>{aiPropertyAnalysis.subjectProperty.sqft.toLocaleString()} sqft</span>
                              )}
                              {aiPropertyAnalysis.subjectProperty.year_built && (
                                <span>Built {aiPropertyAnalysis.subjectProperty.year_built}</span>
                              )}
                            </div>
                            <small className="text-muted d-block mt-1">{aiPropertyAnalysis.subjectProperty.property_type}</small>
                          </div>
                        </div>
                      )}
                      
                      {/* CMA Data Section */}
                      {aiPropertyAnalysis.marketStats && (
                        <div className="cma-data mb-3">
                          <h6 className="fw-semibold mb-2">
                            <FontAwesomeIcon icon={faChartLine} className="text-primary me-2" />
                            Market Statistics
                          </h6>
                          <div className="market-stats-grid">
                            {aiPropertyAnalysis.marketStats.sold_listings && (
                              <div className="stat-item mb-2 p-2 bg-light rounded">
                                <small className="text-muted d-block">Sold Properties</small>
                                <div className="d-flex justify-content-between">
                                  <span className="fw-medium">{aiPropertyAnalysis.marketStats.sold_listings.count} sales</span>
                                  <span className="text-success">${aiPropertyAnalysis.marketStats.sold_listings.avg_price?.toLocaleString()}</span>
                                </div>
                                <small className="text-muted">Avg {aiPropertyAnalysis.marketStats.sold_listings.avg_days_on_market} days on market</small>
                              </div>
                            )}
                            {aiPropertyAnalysis.marketStats.active_listings && (
                              <div className="stat-item mb-2 p-2 bg-light rounded">
                                <small className="text-muted d-block">Active Listings</small>
                                <div className="d-flex justify-content-between">
                                  <span className="fw-medium">{aiPropertyAnalysis.marketStats.active_listings.count} active</span>
                                  <span className="text-primary">${aiPropertyAnalysis.marketStats.active_listings.avg_price?.toLocaleString()}</span>
                                </div>
                                <small className="text-muted">Avg {aiPropertyAnalysis.marketStats.active_listings.avg_days_on_market} days on market</small>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Comparable Properties */}
                      {aiPropertyAnalysis.comparableProperties && aiPropertyAnalysis.comparableProperties.length > 0 && (
                        <div className="comparable-properties mb-3">
                          <h6 className="fw-semibold mb-2">
                            <FontAwesomeIcon icon={faHome} className="text-success me-2" />
                            Recent Comparables ({aiPropertyAnalysis.comparableProperties.length})
                          </h6>
                          <div className="comparables-list">
                            {aiPropertyAnalysis.comparableProperties.slice(0, 3).map((comp, index) => (
                              <div key={index} className="comparable-item mb-2 p-2 bg-light rounded">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <small className="fw-medium d-block">{comp.address}</small>
                                    <small className="text-muted">{comp.beds}bd/{comp.baths}ba • {comp.sqft?.toLocaleString()} sqft</small>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-bold text-success">${comp.price?.toLocaleString()}</div>
                                    <small className="text-muted">${comp.price_per_sqft}/sqft</small>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                                                     <Badge bg={comp.status === 'SOLD' ? 'success' : comp.status === 'ACTIVE' ? 'primary' : 'warning'}>
                                    {comp.status}
                                  </Badge>
                                  <small className="text-muted">{comp.days_on_market} days on market</small>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {aiPropertyAnalysis.recommendations && aiPropertyAnalysis.recommendations.length > 0 && (
                        <div className="recommendations">
                          <h6 className="fw-semibold mb-2">AI Recommendations</h6>
                          <ul className="list-unstyled">
                            {aiPropertyAnalysis.recommendations.map((rec, index) => (
                              <li key={index} className="mb-1">
                                <FontAwesomeIcon icon={faLightbulb} className="text-warning me-2" size="sm" />
                                <small>{rec}</small>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon={faBolt} className="mb-2" size="2x" />
                    <p>No AI analysis available</p>
                    <p className="small">Upload property documents or CMA reports to generate AI insights</p>
                    <Button variant="outline-primary" size="sm">
                      <FontAwesomeIcon icon={faBolt} className="me-1" />
                      Generate Analysis
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <div className="seller-actions">
          <Card style={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(107, 114, 128, 0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <Card.Header>
              <FontAwesomeIcon icon={faTasks} className="text-secondary me-2" />
              <strong>Quick Actions</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={3}>
                  <Button variant="outline-warning" className="w-100" onClick={openSellerModal}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Edit Property
                  </Button>
                </Col>
                <Col md={3}>
                  <Button 
                    variant="outline-info" 
                    className="w-100"
                    onClick={() => {
                      console.log('🏠 [QUICK ACTION] Upload CMA button clicked');
                      document.getElementById('pdf-upload-input')?.click();
                    }}
                  >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    Upload CMA
                  </Button>
                </Col>
                <Col md={3}>
                  <Button variant="outline-success" className="w-100">
                    <FontAwesomeIcon icon={faBolt} className="me-2" />
                    Generate Report
                  </Button>
                </Col>
                <Col md={3}>
                  <Button variant="outline-primary" className="w-100">
                    <FontAwesomeIcon icon={faCalendar} className="me-2" />
                    Schedule Listing
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  };

  if (!isAuthorized) {
    return (
      <Card className={classNames(className)}>
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon={faLightbulb} className="text-muted mb-3" size="3x" />
          <h5 className="text-muted mb-3">AI Assistant Unavailable</h5>
          <p className="text-muted mb-3">
            Please upgrade your subscription to access the Lead AI Assistant.
          </p>
          <Button variant="primary" href="/settings/subscription-settings">
            Upgrade Now
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // Render different interfaces based on lead type
  if (visibleTabs.showBuyer && visibleTabs.showSeller) {
    // Combo lead - render tabbed interface
    return (
      <>
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(1.05); }
              100% { opacity: 1; transform: scale(1); }
            }
            
            .pulse-animation {
              animation: pulse 2s infinite;
            }
            
            .lead-ai-tabs .nav-pills .nav-link {
              border-radius: 25px;
              padding: 12px 20px;
              margin-right: 8px;
              font-weight: 600;
              font-size: 14px;
              border: 2px solid transparent;
              transition: all 0.3s ease;
            }
            
            .lead-ai-tabs .nav-pills .nav-link.active {
              background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
              border-color: rgba(0, 212, 255, 0.3);
              color: white;
              box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
            }
            
            .lead-ai-tabs .nav-pills .nav-link:not(.active) {
              background: rgba(255, 255, 255, 0.1);
              color: #6b7280;
            }
            
            .lead-ai-tabs .nav-pills .nav-link:not(.active):hover {
              background: rgba(0, 212, 255, 0.1);
              color: #00d4ff;
              transform: translateY(-2px);
            }
          `}
        </style>
        <Card 
          className={classNames(className, "h-100 border-0")}
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.2) !important'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-4"
            style={{ 
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px 12px 0 0'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ 
                    width: "48px", 
                    height: "48px",
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faBolt} 
                    className="text-white" 
                    size="lg"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                  />
                </div>
                <div>
                  <h4 
                    className="mb-1 fw-bold"
                    style={{ 
                      color: '#1f2937',
                      textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                      fontSize: '1.1rem'
                    }}
                  >
                    Lead AI Assistant
                  </h4>
                  <p 
                    className="text-muted mb-0"
                    style={{ fontSize: '14px' }}
                  >
                    Comprehensive AI insights for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                  </p>
                </div>
              </div>
              <Badge bg="success" style={{ fontSize: '11px' }}>
                <FontAwesomeIcon icon={faBolt} className="me-1" />
                AI ACTIVE
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-0">
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'market_intelligence')}>
              {/* Tab Navigation */}
              <div className="lead-ai-tabs p-3" style={{ background: 'rgba(248, 249, 250, 0.8)' }}>
                <Nav variant="pills" className="justify-content-center">
                  {visibleTabs.showBuyer && (
                    <Nav.Item>
                      <Nav.Link eventKey="buyer_profile">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Buyer Profile
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {visibleTabs.showSeller && (
                    <Nav.Item>
                      <Nav.Link eventKey="seller_profile">
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                        Seller Profile
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link eventKey="market_intelligence">
                      <FontAwesomeIcon icon={faChartLine} className="me-2" />
                      Market Intelligence
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>
              
              {/* Tab Content */}
              <Tab.Content className="p-4">
                {visibleTabs.showBuyer && (
                  <Tab.Pane eventKey="buyer_profile">
                    {/* DEPRECATED: Old manual BuyerProfileTab - replaced with AI-powered profile builder */}
                    {/* <BuyerProfileTab 
                      leadId={leadDetails?.id || ''} 
                      leadDetails={leadDetails}
                    /> */}
                    
                    {/* New AI-Powered Buyer Profile */}
                    {!buyerProfile ? (
                      /* Show welcome screen when no profile exists */
                      <Card>
                        <Card.Body className="text-center py-5">
                          <div className="mb-4">
                            <FontAwesomeIcon 
                              icon={faUser} 
                              size="4x" 
                              className="text-primary opacity-50"
                            />
                          </div>
                          <h4>AI-Powered Buyer Profile</h4>
                          <p className="text-muted mb-4">
                            Build comprehensive buyer profiles through intelligent conversation
                          </p>
                          <Button 
                            variant="primary"
                            size="lg"
                            onClick={() => {
                              console.log('Opening AI Profile Builder modal');
                              setShowEditModal(true);
                              setEditFormData(prev => ({ ...prev, modalTab: 'ai' }));
                            }}
                          >
                            <FontAwesomeIcon icon={faBolt} className="me-2" />
                            Launch AI Profile Builder
                          </Button>
                          
                          {/* Temporary debug button */}
                          <Button 
                            variant="warning"
                            size="sm"
                            className="ms-2"
                            onClick={() => {
                              console.log('🔧 Manual test of loadBuyerProfile');
                              loadBuyerProfile();
                            }}
                          >
                            Test Load Profile
                          </Button>
                        </Card.Body>
                      </Card>
                    ) : (
                      /* Show profile data when loaded */
                      <div>
                        <Row className="mb-4">
                          <Col md={8}>
                            <h4 className="mb-3">
                              <FontAwesomeIcon icon={faUser} className="me-2" />
                              Buyer Profile for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                            </h4>
                          </Col>
                          <Col md={4} className="text-end">
                            <Button 
                              variant="primary"
                              onClick={() => {
                                setShowEditModal(true);
                                setEditFormData(prev => ({ ...prev, modalTab: 'manual' }));
                              }}
                            >
                              <FontAwesomeIcon icon={faEdit} className="me-2" />
                              Edit Profile
                            </Button>
                          </Col>
                        </Row>
                        
                        {/* Profile Details */}
                        <Row className="g-4">
                          <Col md={6}>
                            <Card>
                              <Card.Header className="bg-light">
                                <h6 className="mb-0">
                                  <FontAwesomeIcon icon={faHome} className="me-2" />
                                  Property Preferences
                                </h6>
                              </Card.Header>
                              <Card.Body>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Property Type:</Col>
                                  <Col sm={7}>{buyerProfile.property_preferences?.property_type || 'Not specified'}</Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Bedrooms:</Col>
                                  <Col sm={7}>{buyerProfile.property_preferences?.bedrooms || 'Not specified'}</Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Bathrooms:</Col>
                                  <Col sm={7}>{buyerProfile.property_preferences?.bathrooms || 'Not specified'}</Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Budget:</Col>
                                  <Col sm={7}>
                                    {buyerProfile.property_preferences?.budget_min || buyerProfile.property_preferences?.budget_max ? (
                                      `$${(buyerProfile.property_preferences?.budget_min || 0).toLocaleString()} - $${(buyerProfile.property_preferences?.budget_max || 0).toLocaleString()}`
                                    ) : 'Not specified'}
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Square Feet:</Col>
                                  <Col sm={7}>
                                    {buyerProfile.property_preferences?.square_feet_min || buyerProfile.property_preferences?.square_feet_max ? (
                                      `${(buyerProfile.property_preferences?.square_feet_min || 0).toLocaleString()} - ${(buyerProfile.property_preferences?.square_feet_max || 0).toLocaleString()} sqft`
                                    ) : 'Not specified'}
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                          
                          <Col md={6}>
                            <Card>
                              <Card.Header className="bg-light">
                                <h6 className="mb-0">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                                  Location & Timeline
                                </h6>
                              </Card.Header>
                              <Card.Body>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Preferred Cities:</Col>
                                  <Col sm={7}>
                                    {buyerProfile.location_preferences?.preferred_cities?.join(', ') || 'Not specified'}
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Timeline:</Col>
                                  <Col sm={7}>
                                    <Badge bg={buyerProfile.buying_timeframe === '0-3 months' ? 'danger' : 'secondary'}>
                                      {buyerProfile.buying_timeframe || 'Not specified'}
                                    </Badge>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Stage:</Col>
                                  <Col sm={7}>
                                    <Badge bg="primary">{buyerProfile.buyer_stage || 'Researching'}</Badge>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">Pre-approved:</Col>
                                  <Col sm={7}>
                                    {buyerProfile.pre_approved ? (
                                      <Badge bg="success">Yes</Badge>
                                    ) : (
                                      <Badge bg="secondary">No</Badge>
                                    )}
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col sm={5} className="text-muted">First-time Buyer:</Col>
                                  <Col sm={7}>
                                    {buyerProfile.first_time_buyer ? 'Yes' : 'No'}
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                        
                        
                        {/* Display saved properties */}
                        {savedProperties.length > 0 && (
                          <div className="mt-4">
                            <h5 className="mb-3">
                              <FontAwesomeIcon icon={faHeart} className="text-danger me-2" />
                              Saved Properties ({savedProperties.length})
                            </h5>
                            <Row className="g-3">
                              {savedProperties.slice(0, 3).map((property, index) => (
                                <Col md={4} key={property.mlsNumber || index}>
                                  <Card className="h-100 shadow-sm">
                                    {property.images && property.images[0] && (
                                      <Card.Img 
                                        variant="top" 
                                        src={property.images[0]} 
                                        style={{ height: '150px', objectFit: 'cover' }}
                                      />
                                    )}
                                    <Card.Body>
                                      <h6>${property.price?.toLocaleString()}</h6>
                                      <small className="text-muted d-block">{property.address}</small>
                                      <small className="text-muted">
                                        {property.beds || property.bedrooms}bd | 
                                        {property.baths || property.bathrooms}ba | 
                                        {property.sqft || property.square_feet || 'N/A'} sqft
                                      </small>
                                      <div className="mt-2">
                                        <small className="text-muted">
                                          Saved: {new Date(property.savedAt).toLocaleDateString()}
                                        </small>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                            {savedProperties.length > 3 && (
                              <div className="text-center mt-3">
                                <Button variant="link" size="sm">
                                  View All {savedProperties.length} Saved Properties
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Tab.Pane>
                )}
                
                {visibleTabs.showSeller && (
                  <Tab.Pane eventKey="seller_profile">
                    <div className="seller-content">
                      {renderSellerProfile()}
                    </div>
                  </Tab.Pane>
                )}
                
                <Tab.Pane eventKey="market_intelligence">
                  <div className="market-content">
                    {renderInsightsOverview()}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        </Card>

        {/* Seller Profile Modal - Combo Lead */}
        {console.log('🔧 [DEBUG] Rendering COMBO seller modal with showSellerModal =', showSellerModal)}
        <Modal 
          show={showSellerModal} 
          onHide={closeSellerModal}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNewSellerLead ? (
                <>🏠 Welcome, {leadDetails?.lead_first_name}! Let's Get Your Property Details</>
              ) : (
                <>📝 Edit Seller Profile - {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</>
              )}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            {sellerModalMode === 'welcome' ? (
              <div className="text-center py-5">
                {isNewSellerLead ? (
                  <>
                    <h3>🎉 Let's Get Started!</h3>
                    <p className="mb-3">To provide you with accurate market insights and property analysis, we'll need some basic information about your property.</p>
                    <div className="alert alert-info">
                      <strong>💡 Quick Setup:</strong> This will only take a few minutes and helps us provide better recommendations!
                    </div>
                  </>
                ) : (
                  <>
                    <h3>📝 Update Your Information</h3>
                    <p className="mb-3">Review and update your seller profile information.</p>
                  </>
                )}
                <hr className="my-4" />
                <p><strong>Lead:</strong> {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</p>
                <p><strong>Lead Type:</strong> {leadDetails?.lead_type?.lead_type || 'Unknown'}</p>
                <div className="text-muted small">
                  Modal functionality confirmed ✅ | API connection working ✅
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="alert alert-success mb-4">
                  <strong>🎉 Great! Now let's collect your property details.</strong>
                </div>
                
                {/* Complete Seller Profile Form */}
                <Form>
                  {/* Property Address Section */}
                  <div className="mb-4">
                    <h5 className="text-primary mb-3">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      Property Information
                    </h5>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-danger" />
                            Property Address *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="1234 Main Street"
                            value={sellerFormData.property_address}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_address: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">City *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Phoenix"
                            value={sellerFormData.property_city}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_city: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">State *</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_state}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_state: e.target.value}))}
                            required
                          >
                            <option value="">Select State</option>
                            {US_STATES.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">ZIP Code *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="85001"
                            value={sellerFormData.property_zip}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_zip: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Property Details Section */}
                  <div className="mb-4">
                    <h5 className="text-success mb-3">
                      <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      Property Details
                    </h5>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Property Type</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_type}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_type: e.target.value}))}
                          >
                            <option value="">Select Type</option>
                            <option value="single_family">Single Family Home</option>
                            <option value="townhouse">Townhouse</option>
                            <option value="condo">Condominium</option>
                            <option value="duplex">Duplex</option>
                            <option value="mobile_home">Mobile Home</option>
                            <option value="land">Land/Lot</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Bedrooms</Form.Label>
                          <Form.Select
                            value={sellerFormData.bedrooms}
                            onChange={(e) => setSellerFormData(prev => ({...prev, bedrooms: e.target.value}))}
                          >
                            <option value="">Select</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5+</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Bathrooms</Form.Label>
                          <Form.Select
                            value={sellerFormData.bathrooms}
                            onChange={(e) => setSellerFormData(prev => ({...prev, bathrooms: e.target.value}))}
                          >
                            <option value="">Select</option>
                            <option value="1">1</option>
                            <option value="1.5">1.5</option>
                            <option value="2">2</option>
                            <option value="2.5">2.5</option>
                            <option value="3">3</option>
                            <option value="3.5">3.5</option>
                            <option value="4">4+</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Square Footage</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="2000"
                            value={sellerFormData.square_footage}
                            onChange={(e) => setSellerFormData(prev => ({...prev, square_footage: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Year Built</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="2010"
                            min="1900"
                            max={new Date().getFullYear()}
                            value={sellerFormData.year_built}
                            onChange={(e) => setSellerFormData(prev => ({...prev, year_built: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Property Condition</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_condition}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_condition: e.target.value}))}
                          >
                            <option value="">Select Condition</option>
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="needs_work">Needs Work</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Pricing & Timeline Section */}
                  <div className="mb-4">
                    <h5 className="text-warning mb-3">
                      <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                      Pricing & Timeline
                    </h5>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Estimated Value</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="450000"
                            value={sellerFormData.estimated_value}
                            onChange={(e) => setSellerFormData(prev => ({...prev, estimated_value: e.target.value}))}
                          />
                          <Form.Text className="text-muted">Your estimate of current market value</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Desired Price</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="475000"
                            value={sellerFormData.desired_price}
                            onChange={(e) => setSellerFormData(prev => ({...prev, desired_price: e.target.value}))}
                          />
                          <Form.Text className="text-muted">What you'd like to get</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Minimum Price</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="425000"
                            value={sellerFormData.minimum_price}
                            onChange={(e) => setSellerFormData(prev => ({...prev, minimum_price: e.target.value}))}
                          />
                          <Form.Text className="text-muted">Lowest acceptable price</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Selling Timeframe</Form.Label>
                          <Form.Select
                            value={sellerFormData.selling_timeframe}
                            onChange={(e) => setSellerFormData(prev => ({...prev, selling_timeframe: e.target.value}))}
                          >
                            <option value="">Select Timeframe</option>
                            <option value="immediately">Immediately (ASAP)</option>
                            <option value="within_30_days">Within 30 days</option>
                            <option value="within_3_months">Within 3 months</option>
                            <option value="within_6_months">Within 6 months</option>
                            <option value="within_1_year">Within 1 year</option>
                            <option value="just_exploring">Just exploring options</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Motivation Level</Form.Label>
                          <Form.Select
                            value={sellerFormData.motivation_level}
                            onChange={(e) => setSellerFormData(prev => ({...prev, motivation_level: e.target.value}))}
                          >
                            <option value="low">Low - Just exploring</option>
                            <option value="medium">Medium - Considering options</option>
                            <option value="high">High - Ready to sell soon</option>
                            <option value="urgent">Urgent - Must sell quickly</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Additional Information Section */}
                  <div className="mb-4">
                    <h5 className="text-info mb-3">
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      Additional Information
                    </h5>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Recent Improvements</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Recent renovations, upgrades, or improvements made to the property..."
                            value={sellerFormData.recent_improvements}
                            onChange={(e) => setSellerFormData(prev => ({...prev, recent_improvements: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Why are you selling?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Reason for selling (relocation, downsizing, upgrading, etc.)..."
                            value={sellerFormData.why_selling}
                            onChange={(e) => setSellerFormData(prev => ({...prev, why_selling: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  <div className="alert alert-success">
                    <strong>✨ Ready to save!</strong> All fields are optional except property address and city.
                  </div>
                </Form>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            {sellerModalMode === 'welcome' ? (
              <>
                <Button variant="outline-secondary" onClick={closeSellerModal}>
                  Close
                </Button>
                <Button 
                  variant="warning"
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [CONTINUE SETUP] Moving to seller profile form');
                    setSellerModalMode('form');
                    console.log('🔧 [CONTINUE SETUP] Modal mode changed to form');
                  }}
                >
                  Continue Setup
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [BACK] Returning to welcome screen');
                    setSellerModalMode('welcome');
                  }}
                >
                  ← Back
                </Button>
                <Button 
                  variant="success"
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [SAVE] Saving seller profile');
                    saveSellerProfile();
                  }}
                  disabled={savingSellerProfile}
                >
                  {savingSellerProfile ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </>
    );
  } else if (visibleTabs.showSeller) {
    // Seller-only lead
    return (
      <>
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(1.05); }
              100% { opacity: 1; transform: scale(1); }
            }
            
            .pulse-animation {
              animation: pulse 2s infinite;
            }
          `}
        </style>
        <Card 
          className={classNames(className, "h-100 border-0")}
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.2) !important'
          }}
        >
          <Card.Header 
            className="border-bottom-0 py-4"
            style={{ 
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px 12px 0 0'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ 
                    width: "48px", 
                    height: "48px",
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faBuilding} 
                    className="text-white" 
                    size="lg"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                  />
                </div>
                <div>
                  <h4 
                    className="mb-1 fw-bold"
                    style={{ 
                      color: '#1f2937',
                      textShadow: '0 0 10px rgba(245, 158, 11, 0.3)',
                      fontSize: '1.1rem'
                    }}
                  >
                    Seller AI Assistant
                  </h4>
                  <p 
                    className="text-muted mb-0"
                    style={{ fontSize: '14px' }}
                  >
                    AI-powered seller insights for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                  </p>
                </div>
              </div>
              <Badge bg="warning" style={{ fontSize: '11px' }}>
                <FontAwesomeIcon icon={faBuilding} className="me-1" />
                SELLER AI
              </Badge>
            </div>
          </Card.Header>
          
          <Card.Body className="p-4" style={{ background: 'transparent' }}>
            {renderSellerProfile()}
          </Card.Body>
        </Card>

        {/* Seller Profile Modal - Seller Only Lead */}
        {console.log('🔧 [DEBUG] Rendering SELLER-ONLY modal with showSellerModal =', showSellerModal)}
        <Modal 
          show={showSellerModal} 
          onHide={closeSellerModal}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNewSellerLead ? (
                <>🏠 Welcome, {leadDetails?.lead_first_name}! Let's Get Your Property Details</>
              ) : (
                <>📝 Edit Seller Profile - {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</>
              )}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            {sellerModalMode === 'welcome' ? (
              <div className="text-center py-5">
                {isNewSellerLead ? (
                  <>
                    <h3>🎉 Let's Get Started!</h3>
                    <p className="mb-3">To provide you with accurate market insights and property analysis, we'll need some basic information about your property.</p>
                    <div className="alert alert-info">
                      <strong>💡 Quick Setup:</strong> This will only take a few minutes and helps us provide better recommendations!
                    </div>
                  </>
                ) : (
                  <>
                    <h3>📝 Update Your Information</h3>
                    <p className="mb-3">Review and update your seller profile information.</p>
                  </>
                )}
                <hr className="my-4" />
                <p><strong>Lead:</strong> {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</p>
                <p><strong>Lead Type:</strong> {leadDetails?.lead_type?.lead_type || 'Unknown'}</p>
                <div className="text-muted small">
                  Modal functionality confirmed ✅ | API connection working ✅
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="alert alert-success mb-4">
                  <strong>🎉 Great! Now let's collect your property details.</strong>
                </div>
                
                {/* Complete Seller Profile Form */}
                <Form>
                  {/* Property Address Section */}
                  <div className="mb-4">
                    <h5 className="text-primary mb-3">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      Property Information
                    </h5>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-danger" />
                            Property Address *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="1234 Main Street"
                            value={sellerFormData.property_address}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_address: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">City *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Phoenix"
                            value={sellerFormData.property_city}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_city: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">State *</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_state}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_state: e.target.value}))}
                            required
                          >
                            <option value="">Select State</option>
                            {US_STATES.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">ZIP Code *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="85001"
                            value={sellerFormData.property_zip}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_zip: e.target.value}))}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Property Details Section */}
                  <div className="mb-4">
                    <h5 className="text-success mb-3">
                      <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      Property Details
                    </h5>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Property Type</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_type}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_type: e.target.value}))}
                          >
                            <option value="">Select Type</option>
                            <option value="single_family">Single Family Home</option>
                            <option value="townhouse">Townhouse</option>
                            <option value="condo">Condominium</option>
                            <option value="duplex">Duplex</option>
                            <option value="mobile_home">Mobile Home</option>
                            <option value="land">Land/Lot</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Bedrooms</Form.Label>
                          <Form.Select
                            value={sellerFormData.bedrooms}
                            onChange={(e) => setSellerFormData(prev => ({...prev, bedrooms: e.target.value}))}
                          >
                            <option value="">Select</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5+</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Bathrooms</Form.Label>
                          <Form.Select
                            value={sellerFormData.bathrooms}
                            onChange={(e) => setSellerFormData(prev => ({...prev, bathrooms: e.target.value}))}
                          >
                            <option value="">Select</option>
                            <option value="1">1</option>
                            <option value="1.5">1.5</option>
                            <option value="2">2</option>
                            <option value="2.5">2.5</option>
                            <option value="3">3</option>
                            <option value="3.5">3.5</option>
                            <option value="4">4+</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Square Footage</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="2000"
                            value={sellerFormData.square_footage}
                            onChange={(e) => setSellerFormData(prev => ({...prev, square_footage: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Year Built</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="2010"
                            min="1900"
                            max={new Date().getFullYear()}
                            value={sellerFormData.year_built}
                            onChange={(e) => setSellerFormData(prev => ({...prev, year_built: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Property Condition</Form.Label>
                          <Form.Select
                            value={sellerFormData.property_condition}
                            onChange={(e) => setSellerFormData(prev => ({...prev, property_condition: e.target.value}))}
                          >
                            <option value="">Select Condition</option>
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="needs_work">Needs Work</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Pricing & Timeline Section */}
                  <div className="mb-4">
                    <h5 className="text-warning mb-3">
                      <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                      Pricing & Timeline
                    </h5>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Estimated Value</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="450000"
                            value={sellerFormData.estimated_value}
                            onChange={(e) => setSellerFormData(prev => ({...prev, estimated_value: e.target.value}))}
                          />
                          <Form.Text className="text-muted">Your estimate of current market value</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Desired Price</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="475000"
                            value={sellerFormData.desired_price}
                            onChange={(e) => setSellerFormData(prev => ({...prev, desired_price: e.target.value}))}
                          />
                          <Form.Text className="text-muted">What you'd like to get</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Minimum Price</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="425000"
                            value={sellerFormData.minimum_price}
                            onChange={(e) => setSellerFormData(prev => ({...prev, minimum_price: e.target.value}))}
                          />
                          <Form.Text className="text-muted">Lowest acceptable price</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Selling Timeframe</Form.Label>
                          <Form.Select
                            value={sellerFormData.selling_timeframe}
                            onChange={(e) => setSellerFormData(prev => ({...prev, selling_timeframe: e.target.value}))}
                          >
                            <option value="">Select Timeframe</option>
                            <option value="immediately">Immediately (ASAP)</option>
                            <option value="within_30_days">Within 30 days</option>
                            <option value="within_3_months">Within 3 months</option>
                            <option value="within_6_months">Within 6 months</option>
                            <option value="within_1_year">Within 1 year</option>
                            <option value="just_exploring">Just exploring options</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Motivation Level</Form.Label>
                          <Form.Select
                            value={sellerFormData.motivation_level}
                            onChange={(e) => setSellerFormData(prev => ({...prev, motivation_level: e.target.value}))}
                          >
                            <option value="low">Low - Just exploring</option>
                            <option value="medium">Medium - Considering options</option>
                            <option value="high">High - Ready to sell soon</option>
                            <option value="urgent">Urgent - Must sell quickly</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Additional Information Section */}
                  <div className="mb-4">
                    <h5 className="text-info mb-3">
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      Additional Information
                    </h5>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Recent Improvements</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Recent renovations, upgrades, or improvements made to the property..."
                            value={sellerFormData.recent_improvements}
                            onChange={(e) => setSellerFormData(prev => ({...prev, recent_improvements: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Why are you selling?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Reason for selling (relocation, downsizing, upgrading, etc.)..."
                            value={sellerFormData.why_selling}
                            onChange={(e) => setSellerFormData(prev => ({...prev, why_selling: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  <div className="alert alert-success">
                    <strong>✨ Ready to save!</strong> All fields are optional except property address and city.
                  </div>
                </Form>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            {sellerModalMode === 'welcome' ? (
              <>
                <Button variant="outline-secondary" onClick={closeSellerModal}>
                  Close
                </Button>
                <Button 
                  variant="warning"
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [CONTINUE SETUP] Moving to seller profile form');
                    setSellerModalMode('form');
                    console.log('🔧 [CONTINUE SETUP] Modal mode changed to form');
                  }}
                >
                  Continue Setup
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [BACK] Returning to welcome screen');
                    setSellerModalMode('welcome');
                  }}
                >
                  ← Back
                </Button>
                <Button 
                  variant="success"
                  onClick={() => {
                    // Debug alert removed
                    console.log('🔧 [SAVE] Saving seller profile');
                    saveSellerProfile();
                  }}
                  disabled={savingSellerProfile}
                >
                  {savingSellerProfile ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // Buyer-only lead (default)
  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          .pulse-animation {
            animation: pulse 2s infinite;
          }
          
          .real-time-indicator {
            position: relative;
          }
          
          .real-time-indicator::before {
            content: '';
            position: absolute;
            top: -2px;
            right: -2px;
            width: 8px;
            height: 8px;
            background: #00d4ff;
            border-radius: 50%;
            animation: pulse 1.5s infinite;
            box-shadow: 0 0 6px rgba(0, 212, 255, 0.6);
          }
          
          .ai-intelligence-center {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
            border-radius: 20px;
            padding: 0;
            border: 1px solid rgba(0, 212, 255, 0.1);
            backdrop-filter: blur(20px);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          }
          
          /* Responsive styles for small screens */
          @media (max-width: 500px) {
            .ai-assistant-header {
              flex-direction: column;
              align-items: flex-start;
            }
            
            .ai-assistant-title {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 100%;
              max-width: 100%;
              font-size: 1rem !important;
            }
            
            .ai-assistant-badge {
              margin-top: 8px;
              align-self: flex-start;
              margin-left: 0;
              margin-right: auto;
              padding: 6px 12px !important;
            }
          }
        `}
      </style>
      <Card 
        className={classNames(className, "h-100 border-0")}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important'
        }}
      >
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px 12px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: "48px", 
                height: "48px",
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
            >
              <FontAwesomeIcon 
                icon={faBolt} 
                className="text-white" 
                size="lg"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
              />
            </div>
            <div>
              <h4 
                className="mb-1 fw-bold ai-assistant-title"
                style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                  fontSize: '1.1rem'
                }}
              >
                Lead AI Insights
              </h4>
              <p 
                className="text-muted mb-0"
                style={{ fontSize: '14px' }}
              >
                AI-powered intelligence for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
              </p>
          </div>
          </div>
          <div 
            className="d-flex align-items-center ai-assistant-badge"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}
          >
            <div 
              className="rounded-circle me-2"
              style={{
                width: '8px',
                height: '8px',
                background: '#10b981',
                animation: 'pulse 2s infinite'
              }}
            ></div>
            <span 
              className="fw-semibold text-success"
              style={{ 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              AI ACTIVE
            </span>
          </div>
        </div>
      </Card.Header>
      
      <Card.Body className="p-4" style={{ background: 'transparent' }}>
        {renderInsightsOverview()}
      </Card.Body>
    </Card>

    {/* Buyer Profile Modal */}
    <BuyerProfileModal
      show={showEditModal}
      onHide={closeEditModal}
      leadId={leadDetails?.id || ''}
      leadName={`${leadDetails?.lead_first_name || ''} ${leadDetails?.lead_last_name || ''}`}
      existingProfile={buyerProfile}
      onSave={async () => {
        await loadBuyerProfile();
        const preferences = buyerProfile?.property_preferences;
        if (Array.isArray(preferences)) {
          setPropertyPreferences(preferences);
        } else if (preferences) {
          setPropertyPreferences([preferences as PropertyPreferenceProps]);
        } else {
          setPropertyPreferences([]);
        }
      }}
    />
    
    {/* Keep original modal but comment it out for now */}
    {false && (
    <Modal 
      show={showEditModal} 
      onHide={closeEditModal}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header 
        closeButton 
        className="border-0" 
        style={{ 
          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
          color: 'white'
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faHome} className="me-2" />
          Complete Buyer Profile - {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-0">
        {/* Tab Navigation */}
        <div className="border-bottom">
          <div className="d-flex">
            <button
              className={`btn btn-link border-0 px-4 py-3 fw-semibold ${
                editFormData.modalTab === 'manual' 
                  ? 'text-primary border-bottom border-primary border-3' 
                  : 'text-muted'
              }`}
              onClick={() => setEditFormData(prev => ({...prev, modalTab: 'manual'}))}
              style={{ 
                borderRadius: 0,
                background: editFormData.modalTab === 'manual' ? 'rgba(0, 212, 255, 0.1)' : 'transparent'
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              Manual Entry
            </button>
            <button
              className={`btn btn-link border-0 px-4 py-3 fw-semibold ${
                editFormData.modalTab === 'ai' 
                  ? 'text-success border-bottom border-success border-3' 
                  : 'text-muted'
              }`}
              onClick={() => setEditFormData(prev => ({...prev, modalTab: 'ai'}))}
              style={{ 
                borderRadius: 0,
                background: editFormData.modalTab === 'ai' ? 'rgba(40, 167, 69, 0.1)' : 'transparent'
              }}
            >
              <span className="me-2" style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '16px' }}>K</span>
              AI Assistant
              {savingProfile && <Spinner size="sm" className="ms-2" />}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {editFormData.modalTab === 'manual' && (
            <div>
              <div className="mb-4">
                <h5 className="text-primary mb-2">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  Property Preferences
                </h5>
                <p className="text-muted mb-4">
                  Define what {leadDetails?.lead_first_name} is looking for in their ideal home.
                </p>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faHome} className="me-1 text-primary" />
                        Property Type
                      </Form.Label>
                      <Form.Select
                        value={editFormData.property_type}
                        onChange={(e) => setEditFormData(prev => ({...prev, property_type: e.target.value}))}
                      >
                        <option value="">Select Type</option>
                        <option value="single_family">Single Family Home</option>
                        <option value="condo">Condominium</option>
                        <option value="townhouse">Townhouse</option>
                        <option value="apartment">Apartment</option>
                        <option value="duplex">Duplex</option>
                        <option value="land">Land/Lot</option>
                        <option value="multi_family">Multi-Family</option>
                        <option value="any">Any Type</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faUser} className="me-1 text-primary" />
                        Bedrooms
                      </Form.Label>
                      <Form.Select
                        value={editFormData.bedrooms}
                        onChange={(e) => setEditFormData(prev => ({...prev, bedrooms: e.target.value}))}
                      >
                        <option value="">Any</option>
                        <option value="1">1 Bedroom</option>
                        <option value="2">2 Bedrooms</option>
                        <option value="3">3 Bedrooms</option>
                        <option value="4">4 Bedrooms</option>
                        <option value="5">5+ Bedrooms</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faUser} className="me-1 text-primary" />
                        Bathrooms
                      </Form.Label>
                      <Form.Select
                        value={editFormData.bathrooms}
                        onChange={(e) => setEditFormData(prev => ({...prev, bathrooms: e.target.value}))}
                      >
                        <option value="">Any</option>
                        <option value="1">1 Bathroom</option>
                        <option value="1.5">1.5 Bathrooms</option>
                        <option value="2">2 Bathrooms</option>
                        <option value="2.5">2.5 Bathrooms</option>
                        <option value="3">3 Bathrooms</option>
                        <option value="3.5">3.5 Bathrooms</option>
                        <option value="4">4+ Bathrooms</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faDollarSign} className="me-1 text-success" />
                        Budget Range
                      </Form.Label>
                      <Row>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Min Budget"
                            value={editFormData.budget_min}
                            onChange={(e) => setEditFormData(prev => ({...prev, budget_min: e.target.value}))}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Max Budget"
                            value={editFormData.budget_max}
                            onChange={(e) => setEditFormData(prev => ({...prev, budget_max: e.target.value}))}
                          />
                        </Col>
                      </Row>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faHome} className="me-1 text-info" />
                        Square Footage
                      </Form.Label>
                      <Row>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Min Sq Ft"
                            value={editFormData.square_feet_min || ''}
                            onChange={(e) => setEditFormData(prev => ({...prev, square_feet_min: e.target.value}))}
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            type="number"
                            placeholder="Max Sq Ft"
                            value={editFormData.square_feet_max || ''}
                            onChange={(e) => setEditFormData(prev => ({...prev, square_feet_max: e.target.value}))}
                          />
                        </Col>
                      </Row>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faClock} className="me-1 text-warning" />
                        Buying Timeline
                      </Form.Label>
                      <Form.Select
                        value={editFormData.timeline}
                        onChange={(e) => setEditFormData(prev => ({...prev, timeline: e.target.value}))}
                      >
                        <option value="">Select Timeline</option>
                        <option value="0-3 months">0-3 months</option>
                        <option value="3-6 months">3-6 months</option>
                        <option value="6-12 months">6-12 months</option>
                        <option value="12+ months">12+ months</option>
                        <option value="Not specified">Not specified</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
                        Pre-approved?
                      </Form.Label>
                      <div className="d-flex gap-3 mt-2">
                        <Form.Check
                          type="radio"
                          id="pre-approved-yes"
                          name="pre-approved"
                          label="Yes"
                          checked={editFormData.pre_approved === 'true'}
                          onChange={() => setEditFormData(prev => ({...prev, pre_approved: 'true'}))}
                        />
                        <Form.Check
                          type="radio"
                          id="pre-approved-no"
                          name="pre-approved"
                          label="No"
                          checked={editFormData.pre_approved === 'false'}
                          onChange={() => setEditFormData(prev => ({...prev, pre_approved: 'false'}))}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Pre-approval Amount and Move-in Date */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faDollarSign} className="me-1 text-success" />
                        Pre-approval Amount
                      </Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Pre-approval amount"
                        value={editFormData.pre_approval_amount}
                        onChange={(e) => setEditFormData(prev => ({...prev, pre_approval_amount: e.target.value}))}
                        disabled={editFormData.pre_approved !== 'true'}
                      />
                      <Form.Text className="text-muted">
                        Only enabled if pre-approved
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FontAwesomeIcon icon={faCalendar} className="me-1 text-info" />
                        Desired Move-in Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.desired_move_in_date}
                        onChange={(e) => setEditFormData(prev => ({...prev, desired_move_in_date: e.target.value}))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <hr className="my-4" />

              <div className="mb-4">
                <h5 className="text-success mb-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                  Location Preferences
                </h5>
                <p className="text-muted mb-4">
                  Specify preferred areas and locations.
                </p>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Preferred Cities/Areas</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter preferred locations (e.g., Phoenix, Scottsdale, Tempe)"
                    value={editFormData.preferred_cities || ''}
                    onChange={(e) => setEditFormData(prev => ({...prev, preferred_cities: e.target.value}))}
                  />
                  <Form.Text className="text-muted">
                    Separate multiple locations with commas
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Work Location</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Primary work location"
                        value={editFormData.work_location || ''}
                        onChange={(e) => setEditFormData(prev => ({...prev, work_location: e.target.value}))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Max Commute Time</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Minutes"
                        value={editFormData.max_commute_time || ''}
                        onChange={(e) => setEditFormData(prev => ({...prev, max_commute_time: e.target.value}))}
                      />
                      <Form.Text className="text-muted">
                        Maximum acceptable commute in minutes
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <hr className="my-4" />

              <div className="mb-4">
                <h5 className="text-info mb-2">
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Additional Information
                </h5>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Motivation for Buying</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="What's driving their home purchase? (e.g., growing family, relocation, investment)"
                    value={editFormData.motivation || ''}
                    onChange={(e) => setEditFormData(prev => ({...prev, motivation: e.target.value}))}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">First-time buyer?</Form.Label>
                      <div className="d-flex gap-3 mt-2">
                        <Form.Check
                          type="radio"
                          id="first-time-yes"
                          name="first-time"
                          label="Yes"
                          checked={editFormData.first_time_buyer === 'true'}
                          onChange={() => setEditFormData(prev => ({...prev, first_time_buyer: 'true'}))}
                        />
                        <Form.Check
                          type="radio"
                          id="first-time-no"
                          name="first-time"
                          label="No"
                          checked={editFormData.first_time_buyer === 'false'}
                          onChange={() => setEditFormData(prev => ({...prev, first_time_buyer: 'false'}))}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Buyer Stage</Form.Label>
                      <Form.Select
                        value={editFormData.buyer_stage || ''}
                        onChange={(e) => setEditFormData(prev => ({...prev, buyer_stage: e.target.value}))}
                      >
                        <option value="">Select Stage</option>
                        <option value="researching">Researching</option>
                        <option value="viewing">Viewing Properties</option>
                        <option value="offering">Making Offers</option>
                        <option value="under_contract">Under Contract</option>
                        <option value="closed">Closed</option>
                        <option value="paused">Paused</option>
                        <option value="lost">Lost</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Additional Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Special requirements, must-haves, deal breakers, etc."
                    value={editFormData.additional_notes}
                    onChange={(e) => setEditFormData(prev => ({...prev, additional_notes: e.target.value}))}
                  />
                </Form.Group>
              </div>
            </div>
          )}

          {editFormData.modalTab === 'ai' && (
            <div style={{ height: '600px' }}>
              <div className="d-flex flex-column h-100">
                <div className="mb-3 p-3 bg-light rounded">
                  <h6 className="text-success mb-2">
                    <span className="me-2" style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '16px' }}>K</span>
                    AI Profile Builder
                  </h6>
                  <p className="mb-0 small text-muted">
                                         I&apos;ll help you build and enhance your client&apos;s buyer profile through intelligent conversation. This creates structured data perfect for AI-powered property search and personalized recommendations.
                  </p>
                </div>
                
                <div className="flex-grow-1 overflow-auto border rounded p-3" style={{ maxHeight: '450px' }}>
                  <div className="mb-3">
                    <div className="d-inline-block p-3 rounded bg-light border" style={{ maxWidth: '85%' }}>
                                             <div><strong>Hi! I&apos;m your Buyer Profile Collection Assistant.</strong></div>
                       <div className="mt-2">I&apos;ll help you systematically gather {leadDetails?.lead_first_name}&apos;s buyer preferences and requirements. This creates structured data that will power your property search and personalized recommendations.</div>
                       <div className="mt-2"><strong>Let&apos;s start with the basics:</strong> What has {leadDetails?.lead_first_name} told you about their timeline? Are they looking to purchase within the next few months, or is this more of a longer-term search?</div>
                    </div>
                    <div className="small text-muted mt-1">
                      {new Date().toLocaleTimeString()}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="input-group">
                    <Form.Control
                      type="text"
                      placeholder="Type your response..."
                      value={editFormData.aiMessage || ''}
                      onChange={(e) => setEditFormData(prev => ({...prev, aiMessage: e.target.value}))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          // Handle AI message send
                          toast('AI workflow functionality coming soon!', { icon: 'ℹ️' });
                        }
                      }}
                    />
                    <Button 
                      variant="primary"
                      onClick={() => toast('AI workflow functionality coming soon!', { icon: 'ℹ️' })}
                      disabled={!editFormData.aiMessage?.trim()}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 bg-light">
        <div className="d-flex justify-content-between w-100">
          <div>
            {editFormData.modalTab === 'manual' && (
              <small className="text-muted">
                <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                This data will be used for AI-powered home search and MLS integration
              </small>
            )}
            {editFormData.modalTab === 'ai' && (
              <small className="text-muted">
                <span className="me-1" style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '14px' }}>K</span>
                AI will extract structured data and update the buyer profile automatically
              </small>
            )}
          </div>
          <div>
            <Button 
              variant="outline-secondary" 
              className="me-2"
              onClick={closeEditModal}
            >
              <FontAwesomeIcon icon={faUser} className="me-1" />
              Cancel
            </Button>
            {editFormData.modalTab === 'manual' && (
              <Button 
                variant="success"
                onClick={saveBuyerPreferences}
                disabled={savingProfile}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                {savingProfile ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Profile'
                )}
              </Button>
            )}
            {editFormData.modalTab === 'ai' && (
              <Button 
                variant="success"
                onClick={() => {
                  closeEditModal();
                  toast.success('AI Profile data will be processed automatically');
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Complete
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
    )}





    {/* Seller Profile Modal */}
    {console.log('🔧 [DEBUG] Rendering seller modal with showSellerModal =', showSellerModal)}
    <Modal 
      show={showSellerModal} 
      onHide={closeSellerModal}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isNewSellerLead ? (
            <>🏠 Welcome, {leadDetails?.lead_first_name}! Let's Get Your Property Details</>
          ) : (
            <>📝 Edit Seller Profile - {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="text-center py-5">
          {isNewSellerLead ? (
            <>
              <h3>🎉 Let's Get Started!</h3>
              <p className="mb-3">To provide you with accurate market insights and property analysis, we'll need some basic information about your property.</p>
              <div className="alert alert-info">
                <strong>💡 Quick Setup:</strong> This will only take a few minutes and helps us provide better recommendations!
              </div>
            </>
          ) : (
            <>
              <h3>📝 Update Your Information</h3>
              <p className="mb-3">Review and update your seller profile information.</p>
            </>
          )}
          <hr className="my-4" />
          <p><strong>Lead:</strong> {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}</p>
          <p><strong>Lead Type:</strong> {leadDetails?.lead_type?.lead_type || 'Unknown'}</p>
          <div className="text-muted small">
            Modal functionality confirmed ✅ | API connection working ✅
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={closeSellerModal}>
          Close
        </Button>
        <Button variant="warning">
          Continue Setup
        </Button>
      </Modal.Footer>
    </Modal>

    </>
  );
};

export default LeadAiAssistant;